import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  type CreateWebinarInput,
  type PageResponse,
  type UpdateWebinarInput,
  webinarService,
} from '@/services/webinar.service';
import { mapApiError, mapErrorMessage, toRejectValue } from '@/lib/mapApiError';
import { resolveApiErrorCode } from '@/lib/subscriptionPaymentResume';
import { fetchAllPages, type FetchedPage } from '@/lib/fetchAllPages';
import { copyOfferConversionInput } from '@/lib/offerEmbed';
import { retryWhileSourceTimelinePreparing } from '@/lib/evergreenConvertError';
import {
  type EvergreenConversionOutcome,
  waitForEvergreenConversion,
} from '@/lib/evergreenLiveSource';
import type {
  CreateIntegrationApiKeyInput,
  EvaluationQuestion,
  EvaluationQuestionInput,
  EvaluationTemplate,
  EvaluationTemplateInput,
  IndexItem,
  IndexItemBulkReplaceInput,
  IndexItemInput,
  IntegrationApiKeyCreatedDto,
  IntegrationApiKeyDto,
  Moderator,
  ModeratorInput,
  OfferLinkTestDto,
  OfferTemplateContent,
  OfferTemplate,
  OfferTemplateContentInput,
  OfferTemplateInput,
  ParticipationStatusBulkInput,
  Presenter,
  PresenterInput,
  PublicSurveySubmission,
  PublicWebinarMe,
  PublicWebinarSession,
  PublicWebinarSubscriptionForm,
  QuitPage,
  QuitPageInput,
  QuitPageReason,
  QuitPageReasonInput,
  QuizAnswerOption,
  QuizAnswerOptionInput,
  QuizQuestion,
  QuizQuestionInput,
  QuizSubmission,
  QuizTemplate,
  QuizTemplateInput,
  AddManualChatMessageInput,
  EvergreenChatConfiguration,
  EvergreenConfiguration,
  EvergreenConfigurationInput,
  UpdateEvergreenChatConfigInput,
  EvergreenTimelineEvent,
  EvergreenTimelineEventInput,
  EvergreenScheduledInteraction,
  EvergreenScheduledInteractionInput,
  EvergreenManualPurchaseNotificationInput,
  ReplayConfiguration,
  ReplayConfigurationInput,
  ResourceUploadInitInput,
  SessionInvite,
  SlideNotesBulkInput,
  SlideNoteUpdateInput,
  SlideUploadInit,
  SlideUploadInitInput,
  SharedResource,
  SharedResourceDownloadUrl,
  SharedResourceFileUploadInitInput,
  SharedResourceLinkInput,
  SharedResourcePage,
  SharedResourcePublic,
  SharedResourceUploadInit,
  Speaker,
  SpeakerInput,
  SurveyAnswerOption,
  SurveyAnswerOptionInput,
  SurveyQuestion,
  SurveyQuestionInput,
  SurveyTemplate,
  SurveyTemplateInput,
  TechnicalAuthUser,
  ThankYouPage,
  ThankYouPageInput,
  WaitingRoomPage,
  WaitingRoomPageInput,
  WebhookDto,
  WebhookInput,
  Webinar,
  WebinarIndex,
  WebinarParticipationStatusConfigDto,
  WebinarResource,
  WebinarResourceFinalizeInput,
  WebinarResourceUploadInit,
  WebinarSession,
  WebinarSessionAttendee,
  SessionAttendeesImportResult,
  SessionAttendeesImportStatus,
  WebinarSessionConfiguration,
  WebinarSessionConfigurationInput,
  WebinarSessionDetail,
  WebinarSlide,
  WebinarSlideFinalizeInput,
  WebinarSlideNote,
  WebinarSubscriptionDto,
  WebinarSubscriptionPage,
  WebinarSubscriptionPageInput,
  WebinarForEvergreen,
  WebinarIntegrationTrigger,
  WebinarIntegrationTriggerInput,
  EvergreenSourcesInput,
} from '@/features/webinar/types';
import type {
  WorkspaceTeamInvitation,
  WorkspaceTeamInviteInput,
  WorkspaceTeamMember,
} from '@/features/workspace/types';
import type { AppError } from '@/features/auth/types';
import { AppError as HttpAppError } from '@/http/errors';
import { readPublicSessionShared } from '@/lib/publicSessionRead';
import type { AppDispatch, RootState } from '@/app/store';
import {
  fetchAdminResourceState,
  triggerAdminEvent,
  updateSessionLayout,
} from '@/features/live/liveThunks';
import { billingService } from '@/services/billing.service';
import { getLiveRolesMax, wouldExceedLiveRolesLimit } from '@/lib/liveRolesLimit';

// Les listes de configuration servent d'état complet côté front : sans pagination explicite,
// on charge toutes les pages, sinon un élément au-delà de la première page passe pour supprimé.
const CONFIGURATION_LIST_PAGE_SIZE = 100;

const listConfigurationEntries = async <T>(
  fetchPage: (page: number, size: number) => Promise<FetchedPage<T>>,
  page?: number,
  size?: number,
): Promise<T[]> => {
  if (page != null || size != null) {
    const response = await fetchPage(page ?? 0, size ?? CONFIGURATION_LIST_PAGE_SIZE);
    return response.content;
  }
  return fetchAllPages((currentPage) => fetchPage(currentPage, CONFIGURATION_LIST_PAGE_SIZE));
};

export const createWebinar = createAsyncThunk<
  number,
  { workspaceId: number; input: CreateWebinarInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>('webinar/createWebinar', async ({ workspaceId, input }, { rejectWithValue, dispatch }) => {
  try {
    const createdWebinarId = await webinarService.create(workspaceId, input);
    if (!createdWebinarId) {
      return rejectWithValue({
        code: 'WEBINAR_ID_NOT_FOUND',
        message: 'errors.generic',
      });
    }

    dispatch(fetchWebinars({ workspaceId }));
    return createdWebinarId;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const testWebhook = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; webhookId: number },
  { rejectValue: AppError }
>('webinar/testWebhook', async (params, { rejectWithValue }) => {
  try {
    await webinarService.testWebhook(params.workspaceId, params.webinarId, params.webhookId);
  } catch (err) {
    return rejectWithValue(toRejectValue(err));
  }
});

export const fetchOfferTestButtonLink = createAsyncThunk<
  OfferLinkTestDto,
  { workspaceId: number; webinarId: number; templateId: number; contentType: string },
  { rejectValue: AppError }
>('webinar/fetchOfferTestButtonLink', async (params, { rejectWithValue }) => {
  try {
    return await webinarService.getOfferTestButtonLink(
      params.workspaceId,
      params.webinarId,
      params.templateId,
      params.contentType,
    );
  } catch (err) {
    return rejectWithValue(toRejectValue(err));
  }
});

export const fetchOfferTestEmbed = createAsyncThunk<
  OfferTemplateContent,
  { workspaceId: number; webinarId: number; templateId: number; contentType: string },
  { rejectValue: AppError }
>('webinar/fetchOfferTestEmbed', async (params, { rejectWithValue }) => {
  try {
    return await webinarService.getOfferTestEmbed(
      params.workspaceId,
      params.webinarId,
      params.templateId,
      params.contentType,
    );
  } catch (err) {
    return rejectWithValue(toRejectValue(err));
  }
});

export const duplicateWebinar = createAsyncThunk<
  number,
  { workspaceId: number; sourceWebinarId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/duplicateWebinar',
  async ({ workspaceId, sourceWebinarId }, { rejectWithValue, dispatch }) => {
    try {
      const usage = await billingService.getUsage(workspaceId);
      const maxLiveRoles = getLiveRolesMax(usage);
      if (maxLiveRoles != null) {
        const sourcePresenters = await webinarService.listPresenters(workspaceId, sourceWebinarId);

        let sourceGuestsCount = 0;
        let guestPage = 0;
        const guestPageSize = 15;
        let hasMoreGuests = true;
        while (hasMoreGuests) {
          const page = await webinarService.listSpeakers(
            workspaceId,
            sourceWebinarId,
            guestPage,
            guestPageSize,
          );
          sourceGuestsCount += page.content.length;
          hasMoreGuests = page.content.length === guestPageSize;
          guestPage++;
        }

        if (wouldExceedLiveRolesLimit(sourcePresenters.length, sourceGuestsCount, maxLiveRoles)) {
          return rejectWithValue({
            code: 'LIVE_ROLES_LIMIT_EXCEEDED',
            message: 'errors.duplicationLiveRolesExceeded',
          });
        }
      }

      const sourceWebinar = await webinarService.getById(workspaceId, sourceWebinarId);

      const newWebinarId = await webinarService.create(workspaceId, {
        title: sourceWebinar.title,
        shortDescription: sourceWebinar.shortDescription,
        description: sourceWebinar.description,
      });

      if (!newWebinarId) {
        return rejectWithValue({
          code: 'WEBINAR_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      try {
        const sourceSessionConfig = await webinarService.getWebinarSession(
          workspaceId,
          sourceWebinarId,
        );

        await webinarService.updateSessionConfiguration(workspaceId, newWebinarId, {
          type: sourceSessionConfig.type,
          nextDates: sourceSessionConfig.nextDates ?? null,
        });
      } catch (sessionError) {
        console.warn('Impossible de dupliquer la session configuration:', sessionError);
      }

      try {
        const allSpeakers: Speaker[] = [];
        let speakerPage = 0;
        const speakerPageSize = 100;
        let hasMoreSpeakers = true;

        while (hasMoreSpeakers) {
          const speakersPage = await webinarService.listSpeakers(
            workspaceId,
            sourceWebinarId,
            speakerPage,
            speakerPageSize,
          );
          allSpeakers.push(...speakersPage.content);
          hasMoreSpeakers = speakersPage.content.length === speakerPageSize;
          speakerPage++;
        }

        for (const speaker of allSpeakers) {
          try {
            const speakerInput: SpeakerInput = {
              overrideFullName: speaker.displayName,
              jobTitle: speaker.jobTitle,
              userId: speaker.userId ?? null,
              guestFullName: speaker.guestFullName ?? null,
              guestEmail: speaker.guestEmail ?? null,
            };
            await webinarService.createSpeaker(workspaceId, newWebinarId, speakerInput);
          } catch (speakerError) {
            console.warn('Impossible de dupliquer le speaker:', speaker, speakerError);
          }
        }
      } catch (speakersError) {
        console.warn('Impossible de dupliquer les speakers:', speakersError);
      }

      try {
        const allModerators: Moderator[] = [];
        let moderatorPage = 0;
        const moderatorPageSize = 100;
        let hasMoreModerators = true;

        while (hasMoreModerators) {
          const moderatorsPage = await webinarService.listModerators(
            workspaceId,
            sourceWebinarId,
            moderatorPage,
            moderatorPageSize,
          );
          allModerators.push(...moderatorsPage.content);
          hasMoreModerators = moderatorsPage.content.length === moderatorPageSize;
          moderatorPage++;
        }

        for (const moderator of allModerators) {
          try {
            const moderatorInput: ModeratorInput = {
              overrideFullName: moderator.displayName,
              jobTitle: moderator.jobTitle,
              userId: moderator.userId,
            };
            await webinarService.createModerator(workspaceId, newWebinarId, moderatorInput);
          } catch (moderatorError) {
            console.warn('Impossible de dupliquer le modérateur:', moderator, moderatorError);
          }
        }
      } catch (moderatorsError) {
        console.warn('Impossible de dupliquer les modérateurs:', moderatorsError);
      }

      try {
        const sourceSubscriptionPage = await webinarService.getSubscriptionPage(
          workspaceId,
          sourceWebinarId,
        );

        await webinarService.updateSubscriptionPage(workspaceId, newWebinarId, {
          firstNameField: sourceSubscriptionPage.firstNameField,
          lastNameField: sourceSubscriptionPage.lastNameField,
          emailField: sourceSubscriptionPage.emailField,
          phoneField: sourceSubscriptionPage.phoneField,
          firstNameMandatory: sourceSubscriptionPage.firstNameMandatory,
          lastNameMandatory: sourceSubscriptionPage.lastNameMandatory,
          emailMandatory: sourceSubscriptionPage.emailMandatory,
          phoneMandatory: sourceSubscriptionPage.phoneMandatory,
          displaySpeakers: sourceSubscriptionPage.displaySpeakers,
          background: sourceSubscriptionPage.background,
          backgroundColor: sourceSubscriptionPage.backgroundColor,
          backgroundGradient: sourceSubscriptionPage.backgroundGradient,
          displayLogo: sourceSubscriptionPage.displayLogo,
          ctaButtonLabel: sourceSubscriptionPage.ctaButtonLabel,
        });

        if (
          sourceSubscriptionPage.background === 'IMAGE' &&
          sourceSubscriptionPage.backgroundImageUrl
        ) {
          try {
            const imageResponse = await fetch(sourceSubscriptionPage.backgroundImageUrl);
            if (!imageResponse.ok) {
              throw new Error(`Failed to fetch image: ${imageResponse.statusText}`);
            }

            const imageBlob = await imageResponse.blob();
            const urlParts = sourceSubscriptionPage.backgroundImageUrl.split('/');
            const fileName = urlParts[urlParts.length - 1]?.split('?')[0] || 'background-image.jpg';
            const imageFile = new File([imageBlob], fileName, { type: imageBlob.type });

            await webinarService.uploadBackgroundImage(workspaceId, newWebinarId, imageFile);
          } catch (imageError) {
            console.warn("Impossible de dupliquer l'image de background:", imageError);
          }
        }
      } catch (subscriptionPageError) {
        console.warn('Impossible de dupliquer la subscription page:', subscriptionPageError);
      }

      try {
        const sourceWaitingRoomPage = await webinarService.getWaitingRoomPage(
          workspaceId,
          sourceWebinarId,
        );

        await webinarService.updateWaitingRoomPage(workspaceId, newWebinarId, {
          displayLogo: sourceWaitingRoomPage.displayLogo,
          displaySpeakers: sourceWaitingRoomPage.displaySpeakers,
          displayGuests: sourceWaitingRoomPage.displayGuests,
          displayIndex: sourceWaitingRoomPage.displayIndex,
          displayParticipantsCountEnabled: sourceWaitingRoomPage.displayParticipantsCountEnabled,
          displayParticipantsMinThreshold: sourceWaitingRoomPage.displayParticipantsMinThreshold,
          background: sourceWaitingRoomPage.background,
          backgroundColor: sourceWaitingRoomPage.backgroundColor,
          backgroundGradient: sourceWaitingRoomPage.backgroundGradient,
          interactionType: sourceWaitingRoomPage.interactionType,
        });

        if (
          sourceWaitingRoomPage.background === 'IMAGE' &&
          sourceWaitingRoomPage.backgroundImageUrl
        ) {
          try {
            const imageResponse = await fetch(sourceWaitingRoomPage.backgroundImageUrl);
            if (!imageResponse.ok) {
              throw new Error(`Failed to fetch image: ${imageResponse.statusText}`);
            }

            const imageBlob = await imageResponse.blob();
            const urlParts = sourceWaitingRoomPage.backgroundImageUrl.split('/');
            const fileName =
              urlParts[urlParts.length - 1]?.split('?')[0] || 'waiting-room-background-image.jpg';
            const imageFile = new File([imageBlob], fileName, { type: imageBlob.type });

            await webinarService.uploadWaitingRoomPageBackgroundImage(
              workspaceId,
              newWebinarId,
              imageFile,
            );
          } catch (imageError) {
            console.warn(
              "Impossible de dupliquer l'image de background de la waiting room:",
              imageError,
            );
          }
        }
      } catch (waitingRoomError) {
        console.warn('Impossible de dupliquer la waiting room page:', waitingRoomError);
      }

      try {
        const sourceIndexItems = await webinarService.listIndexItems(workspaceId, sourceWebinarId);

        if (sourceIndexItems.length > 0) {
          await webinarService.replaceIndexItems(workspaceId, newWebinarId, {
            items: sourceIndexItems.map((item) => ({
              title: item.title,
              description: item.description,
              position: item.position,
            })),
          });
        }
      } catch (indexError) {
        console.warn("Impossible de dupliquer l'index:", indexError);
      }

      try {
        const allSlides: WebinarSlide[] = [];
        let slidePage = 0;
        const slidePageSize = 100;
        let hasMoreSlides = true;

        while (hasMoreSlides) {
          const slidesPage = await webinarService.listSlides(
            workspaceId,
            sourceWebinarId,
            slidePage,
            slidePageSize,
          );
          allSlides.push(...slidesPage.content);
          hasMoreSlides = slidesPage.content.length === slidePageSize;
          slidePage++;
        }

        const pdfSlides = allSlides.filter((slide) => slide.mimeType === 'application/pdf');

        for (const pdfSlide of pdfSlides) {
          try {
            const pdfResponse = await fetch(pdfSlide.slideUrl);
            if (!pdfResponse.ok) {
              throw new Error(`Failed to fetch PDF: ${pdfResponse.statusText}`);
            }

            const pdfBlob = await pdfResponse.blob();
            const pdfFile = new File([pdfBlob], pdfSlide.fileName, { type: pdfSlide.mimeType });

            const uploadInit = await webinarService.generateSlideUploadUrl(
              workspaceId,
              newWebinarId,
              {
                fileName: pdfSlide.fileName,
                mimeType: pdfSlide.mimeType,
                sizeBytes: pdfSlide.sizeBytes,
              },
            );

            const uploadResponse = await fetch(uploadInit.uploadUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': pdfFile.type,
                'Cache-Control': 'public, max-age=31536000, immutable',
              },
              body: pdfFile,
            });

            if (!uploadResponse.ok) {
              throw new Error(`Failed to upload PDF: ${uploadResponse.statusText}`);
            }

            const newSlideId = await webinarService.finalizeSlideUpload(workspaceId, newWebinarId, {
              slideKey: uploadInit.slideKey,
              fileName: pdfSlide.fileName,
              mimeType: pdfSlide.mimeType,
              sizeBytes: pdfSlide.sizeBytes,
              pageCount: pdfSlide.pageCount ?? null,
              checksumSha256: pdfSlide.checksumSha256,
              position: pdfSlide.position,
            });

            if (newSlideId) {
              try {
                const slideNotes = await webinarService.listSlideNotes(
                  workspaceId,
                  sourceWebinarId,
                  pdfSlide.id,
                );

                if (slideNotes.length > 0) {
                  await webinarService.bulkUpsertSlideNotes(workspaceId, newWebinarId, newSlideId, {
                    notes: slideNotes.map((note) => ({
                      pageNumber: note.pageNumber,
                      text: note.text,
                    })),
                  });
                }
              } catch (notesError) {
                console.warn(
                  `Impossible de dupliquer les notes pour le slide ${pdfSlide.id}:`,
                  notesError,
                );
              }
            }
          } catch (slideError) {
            console.warn('Impossible de dupliquer le slide PDF:', pdfSlide, slideError);
          }
        }
      } catch (slidesError) {
        console.warn('Impossible de dupliquer les slides:', slidesError);
      }

      try {
        const allResources: WebinarResource[] = [];
        let resourcePage = 0;
        const resourcePageSize = 100;
        let hasMoreResources = true;

        while (hasMoreResources) {
          const resourcesPage = await webinarService.listResources(
            workspaceId,
            sourceWebinarId,
            resourcePage,
            resourcePageSize,
          );
          allResources.push(...resourcesPage.content);
          hasMoreResources = resourcesPage.content.length === resourcePageSize;
          resourcePage++;
        }

        const videoResources = allResources.filter((resource) =>
          resource.mimeType.startsWith('video/'),
        );

        for (const videoResource of videoResources) {
          try {
            const videoResponse = await fetch(videoResource.resourceUrl);
            if (!videoResponse.ok) {
              throw new Error(`Failed to fetch video: ${videoResponse.statusText}`);
            }

            const videoBlob = await videoResponse.blob();
            const videoFile = new File([videoBlob], videoResource.fileName, {
              type: videoResource.mimeType,
            });

            const uploadInit = await webinarService.initResourceUpload(workspaceId, newWebinarId, {
              fileName: videoResource.fileName,
              mimeType: videoResource.mimeType,
              sizeBytes: videoResource.sizeBytes,
            });

            const uploadResponse = await fetch(uploadInit.uploadUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': videoFile.type,
                'Cache-Control': 'public, max-age=31536000, immutable',
              },
              body: videoFile,
            });

            if (!uploadResponse.ok) {
              throw new Error(`Failed to upload video: ${uploadResponse.statusText}`);
            }

            await webinarService.finalizeResourceUpload(workspaceId, newWebinarId, {
              resourceKey: uploadInit.resourceKey,
              fileName: videoResource.fileName,
              mimeType: videoResource.mimeType,
              sizeBytes: videoResource.sizeBytes,
              pageCount: videoResource.pageCount,
              checksumSha256: videoResource.checksumSha256,
              position: videoResource.position,
            });
          } catch (resourceError) {
            console.warn(
              'Impossible de dupliquer la ressource vidéo:',
              videoResource,
              resourceError,
            );
          }
        }
      } catch (resourcesError) {
        console.warn('Impossible de dupliquer les resources:', resourcesError);
      }

      try {
        const surveyPageSize = 100;
        const surveyTemplatesPage = await webinarService.listSurveyTemplates(
          workspaceId,
          sourceWebinarId,
          0,
          surveyPageSize,
        );

        for (const template of surveyTemplatesPage.content) {
          try {
            const newTemplateId = await webinarService.createSurveyTemplate(
              workspaceId,
              newWebinarId,
              {
                title: template.title,
                subTitle: template.subTitle ?? null,
                borderColor: template.borderColor ?? null,
                accentColor: template.accentColor ?? null,
                backgroundColor: template.backgroundColor ?? null,
                titleColor: template.titleColor ?? null,
                waitingRoomSurvey: template.waitingRoomSurvey,
                displayWaitingRoomSurvey: template.displayWaitingRoomSurvey ?? true,
              },
            );

            if (!newTemplateId) {
              console.warn(
                'Impossible de créer le template de survey pour le nouveau webinar',
                template,
              );
              continue;
            }

            const sourceQuestions = await webinarService.listSurveyQuestions(
              workspaceId,
              sourceWebinarId,
              template.id,
            );

            for (const question of sourceQuestions) {
              try {
                const questionInput: SurveyQuestionInput = {
                  textContent: question.textContent,
                  type: question.type,
                  position: question.position ?? null,
                  required: question.required ?? null,
                  maxSelections: question.maxSelections ?? null,
                  randomizeOptions: question.randomizeOptions ?? null,
                };

                const newQuestionId = await webinarService.createSurveyQuestion(
                  workspaceId,
                  newWebinarId,
                  newTemplateId,
                  questionInput,
                );

                if (!newQuestionId) {
                  console.warn(
                    "Impossible de récupérer l'id de la nouvelle question de survey",
                    question,
                  );
                  continue;
                }

                const sourceOptions = await webinarService.listSurveyAnswerOptions(
                  workspaceId,
                  sourceWebinarId,
                  template.id,
                  question.id,
                );

                for (const option of sourceOptions) {
                  try {
                    const optionInput: SurveyAnswerOptionInput = {
                      textContent: option.textContent,
                      position: option.position ?? null,
                    };

                    await webinarService.createSurveyAnswerOption(
                      workspaceId,
                      newWebinarId,
                      newTemplateId,
                      newQuestionId,
                      optionInput,
                    );
                  } catch (optionError) {
                    console.warn(
                      "Impossible de dupliquer une option d'une survey:",
                      option,
                      optionError,
                    );
                  }
                }
              } catch (questionError) {
                console.warn(
                  "Impossible de dupliquer une question d'une survey:",
                  question,
                  questionError,
                );
              }
            }
          } catch (templateError) {
            console.warn(
              'Impossible de dupliquer un template de survey pour le nouveau webinar:',
              template,
              templateError,
            );
          }
        }
      } catch (waitingRoomSurveyError) {
        console.warn('Impossible de dupliquer les surveys:', waitingRoomSurveyError);
      }

      try {
        const quizPageSize = 100;
        const quizTemplatesPage = await webinarService.listQuizTemplates(
          workspaceId,
          sourceWebinarId,
          0,
          quizPageSize,
        );

        for (const quizTemplate of quizTemplatesPage.content) {
          try {
            const newQuizTemplateId = await webinarService.createQuizTemplate(
              workspaceId,
              newWebinarId,
              {
                name: quizTemplate.name,
                enabledDuringLive: quizTemplate.enabledDuringLive,
                shuffleQuestions: quizTemplate.shuffleQuestions,
                shuffleOptions: quizTemplate.shuffleOptions,
                passingScore: quizTemplate.passingScore,
                visibleFrom: quizTemplate.visibleFrom,
                visibleUntil: quizTemplate.visibleUntil,
                published: quizTemplate.published,
                borderColor: quizTemplate.borderColor ?? null,
                accentColor: quizTemplate.accentColor ?? null,
                backgroundColor: quizTemplate.backgroundColor ?? null,
                titleColor: quizTemplate.titleColor ?? null,
              },
            );

            if (!newQuizTemplateId) {
              console.warn(
                'Impossible de créer le template de quiz pour le nouveau webinar',
                quizTemplate,
              );
              continue;
            }

            const sourceQuizQuestions = await webinarService.listQuizQuestions(
              workspaceId,
              sourceWebinarId,
              quizTemplate.id,
            );

            for (const question of sourceQuizQuestions) {
              try {
                const quizQuestionInput: QuizQuestionInput = {
                  textContent: question.textContent,
                  type: question.type,
                  position: question.position ?? null,
                  points: question.points ?? null,
                  timeLimitSeconds: question.timeLimitSeconds ?? null,
                };

                const newQuizQuestionId = await webinarService.createQuizQuestion(
                  workspaceId,
                  newWebinarId,
                  newQuizTemplateId,
                  quizQuestionInput,
                );

                if (!newQuizQuestionId) {
                  console.warn(
                    "Impossible de récupérer l'id de la nouvelle question de quiz",
                    question,
                  );
                  continue;
                }

                const sourceQuizOptions = await webinarService.listQuizAnswerOptions(
                  workspaceId,
                  sourceWebinarId,
                  quizTemplate.id,
                  question.id,
                );

                for (const option of sourceQuizOptions) {
                  try {
                    const quizOptionInput: QuizAnswerOptionInput = {
                      textContent: option.textContent,
                      correct: option.correct ?? null,
                      position: option.position ?? null,
                    };

                    await webinarService.createQuizAnswerOption(
                      workspaceId,
                      newWebinarId,
                      newQuizTemplateId,
                      newQuizQuestionId,
                      quizOptionInput,
                    );
                  } catch (quizOptionError) {
                    console.warn(
                      "Impossible de dupliquer une option d'un quiz:",
                      option,
                      quizOptionError,
                    );
                  }
                }
              } catch (quizQuestionError) {
                console.warn(
                  "Impossible de dupliquer une question d'un quiz:",
                  question,
                  quizQuestionError,
                );
              }
            }
          } catch (quizTemplateError) {
            console.warn(
              'Impossible de dupliquer un template de quiz pour le nouveau webinar:',
              quizTemplate,
              quizTemplateError,
            );
          }
        }
      } catch (quizzesError) {
        console.warn('Impossible de dupliquer les quizzes:', quizzesError);
      }

      try {
        const offerPageSize = 100;
        const offerTemplatesPage = await webinarService.listOfferTemplates(
          workspaceId,
          sourceWebinarId,
          0,
          offerPageSize,
        );

        for (const template of offerTemplatesPage.content) {
          try {
            const newOfferTemplateId = await webinarService.createOfferTemplate(
              workspaceId,
              newWebinarId,
              {
                privateTitle: template.privateTitle,
                abTesting: template.abTesting,
                splitPercentA: template.splitPercentA,
                thankYouPageOffer: template.thankYouPageOffer,
              },
            );

            if (!newOfferTemplateId) {
              console.warn(
                'Impossible de créer le template d’offre pour le nouveau webinar',
                template,
              );
              continue;
            }

            for (const content of template.contents) {
              try {
                const contentInput: OfferTemplateContentInput = {
                  title: content.title,
                  subTitle: content.subTitle ?? null,
                  description: content.description ?? null,
                  buttonLabel: content.buttonLabel,
                  ...copyOfferConversionInput(content),
                  backgroundColor: content.backgroundColor,
                  borderColor: content.borderColor,
                  titleColor: content.titleColor,
                  textColor: content.textColor,
                  buttonColor: content.buttonColor,
                  buttonTextColor: content.buttonTextColor,
                  icon: content.icon ?? null,
                  iconColor: content.iconColor ?? null,
                  iconBackgroundColor: content.iconBackgroundColor ?? null,
                };

                await webinarService.upsertOfferTemplateContent(
                  workspaceId,
                  newWebinarId,
                  newOfferTemplateId,
                  content.type,
                  contentInput,
                );

                if (content.imageUrl) {
                  try {
                    const imageResponse = await fetch(content.imageUrl);
                    if (!imageResponse.ok) {
                      throw new Error(`Failed to fetch offer image: ${imageResponse.statusText}`);
                    }

                    const imageBlob = await imageResponse.blob();
                    const urlParts = content.imageUrl.split('/');
                    const fileName =
                      urlParts[urlParts.length - 1]?.split('?')[0] || 'offer-content-image.jpg';
                    const imageFile = new File([imageBlob], fileName, { type: imageBlob.type });

                    await webinarService.uploadOfferTemplateContentImage(
                      workspaceId,
                      newWebinarId,
                      newOfferTemplateId,
                      content.type,
                      imageFile,
                    );
                  } catch (offerImageError) {
                    console.warn(
                      "Impossible de dupliquer l'image d'une offre:",
                      content,
                      offerImageError,
                    );
                  }
                }
              } catch (contentError) {
                console.warn('Impossible de dupliquer un contenu d’offre:', content, contentError);
              }
            }
          } catch (offerTemplateError) {
            console.warn(
              'Impossible de dupliquer un template d’offre pour le nouveau webinar:',
              template,
              offerTemplateError,
            );
          }
        }
      } catch (offersError) {
        console.warn('Impossible de dupliquer les offres:', offersError);
      }

      try {
        const sourceQuitPage = await webinarService.getQuitPage(workspaceId, sourceWebinarId);

        await webinarService.upsertQuitPage(workspaceId, newWebinarId, {
          title: sourceQuitPage.title,
          questionText: sourceQuitPage.questionText,
          type: sourceQuitPage.type,
          otherReason: sourceQuitPage.otherReason,
          showPopup: sourceQuitPage.showPopup,
          backgroundColor: sourceQuitPage.backgroundColor,
          textColor: sourceQuitPage.textColor,
          buttonColor: sourceQuitPage.buttonColor,
          buttonTextColor: sourceQuitPage.buttonTextColor,
        });

        let sourceReasons: QuitPageReason[] = sourceQuitPage.reasons ?? [];
        if (sourceReasons.length === 0) {
          sourceReasons = await webinarService.listQuitPageReasons(workspaceId, sourceWebinarId);
        }

        for (const reason of sourceReasons) {
          try {
            const reasonInput: QuitPageReasonInput = {
              reason: reason.reason,
              explanationText: reason.explanationText,
              position: reason.position ?? null,
              imageAlt: reason.imageAlt ?? null,
              displayQrCode: reason.displayQrCode ?? false,
            };

            const newReasonId = await webinarService.createQuitPageReason(
              workspaceId,
              newWebinarId,
              reasonInput,
            );

            if (newReasonId && reason.imageUrl) {
              try {
                const imageResponse = await fetch(reason.imageUrl);
                if (!imageResponse.ok) {
                  throw new Error(
                    `Failed to fetch quit page reason image: ${imageResponse.statusText}`,
                  );
                }

                const imageBlob = await imageResponse.blob();
                const urlParts = reason.imageUrl.split('/');
                const fileName =
                  urlParts[urlParts.length - 1]?.split('?')[0] || 'quit-reason-image.jpg';
                const imageFile = new File([imageBlob], fileName, { type: imageBlob.type });

                await webinarService.uploadQuitPageReasonImage(
                  workspaceId,
                  newWebinarId,
                  newReasonId,
                  imageFile,
                );
              } catch (imageError) {
                console.warn(
                  "Impossible de dupliquer l'image d'une reason de quit page:",
                  reason,
                  imageError,
                );
              }
            }
          } catch (reasonError) {
            console.warn('Impossible de dupliquer une reason de quit page:', reason, reasonError);
          }
        }
      } catch (quitPageError) {
        console.warn('Impossible de dupliquer la quit page:', quitPageError);
      }

      try {
        const sourceThankYouPage = await webinarService.getThankYouPage(
          workspaceId,
          sourceWebinarId,
        );

        await webinarService.updateThankYouPage(workspaceId, newWebinarId, {
          displaySocialMedia: sourceThankYouPage.displaySocialMedia,
          displaySpeakerProfile: sourceThankYouPage.displaySpeakerProfile,
          displayOffer: sourceThankYouPage.displayOffer,
          displayImage: sourceThankYouPage.displayImage,
          displayEvaluation: sourceThankYouPage.displayEvaluation,
          displayLogo: sourceThankYouPage.displayLogo,
        });

        if (sourceThankYouPage.displayImage && sourceThankYouPage.imageUrl) {
          try {
            const imageResponse = await fetch(sourceThankYouPage.imageUrl);
            if (!imageResponse.ok) {
              throw new Error(`Failed to fetch thank you page image: ${imageResponse.statusText}`);
            }

            const imageBlob = await imageResponse.blob();
            const urlParts = sourceThankYouPage.imageUrl.split('/');
            const fileName =
              urlParts[urlParts.length - 1]?.split('?')[0] || 'thank-you-page-image.jpg';
            const imageFile = new File([imageBlob], fileName, { type: imageBlob.type });

            await webinarService.uploadThankYouPageImage(workspaceId, newWebinarId, imageFile);
          } catch (imageError) {
            console.warn("Impossible de dupliquer l'image de la thank you page:", imageError);
          }
        }
      } catch (thankYouError) {
        console.warn('Impossible de dupliquer la thank you page:', thankYouError);
      }

      try {
        const sourceReplayConfig = await webinarService.getReplayConfiguration(
          workspaceId,
          sourceWebinarId,
        );

        await webinarService.updateReplayConfiguration(workspaceId, newWebinarId, {
          active: sourceReplayConfig.active,
          noLimitDate: sourceReplayConfig.noLimitDate,
          limitDate: sourceReplayConfig.limitDate,
          replayType: sourceReplayConfig.replayType,
          externalLink: sourceReplayConfig.externalLink,
          displayVideoCommands: sourceReplayConfig.displayVideoCommands,
          allowComments: sourceReplayConfig.allowComments,
          validityDays: sourceReplayConfig.validityDays,
        });
      } catch (replayError) {
        console.warn('Impossible de dupliquer la replay configuration:', replayError);
      }

      dispatch(fetchWebinars({ workspaceId }));
      return newWebinarId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchAvailableWebinars = createAsyncThunk<
  { content: WebinarForEvergreen[]; totalElements: number; totalPages: number },
  { workspaceId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/fetchAvailableWebinars',
  async ({ workspaceId, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await webinarService.getAvailableWebinars(workspaceId, page, size);
      return {
        content: response.content,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      };
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWebinars = createAsyncThunk<
  PageResponse<Webinar>,
  { workspaceId: number; page?: number; size?: number },
  { rejectValue: AppError }
>('webinar/fetchWebinars', async ({ workspaceId, page, size = 10 }, { rejectWithValue }) => {
  try {
    return await webinarService.getAll(workspaceId, page, size);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchSessionInvites = createAsyncThunk<
  SessionInvite[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchSessionInvites', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    return await webinarService.getInvites(workspaceId, webinarId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchParticipationStatuses = createAsyncThunk<
  WebinarParticipationStatusConfigDto,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchParticipationStatuses', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    return await webinarService.getParticipationStatuses(workspaceId, webinarId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const replaceParticipationStatuses = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; input: ParticipationStatusBulkInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/replaceParticipationStatuses',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.replaceParticipationStatuses(workspaceId, webinarId, input);
      dispatch(fetchParticipationStatuses({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWebinarById = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number },
  { rejectValue: AppError }
>('webinar/fetchWebinarById', async ({ workspaceId, id }, { rejectWithValue }) => {
  try {
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const updateWebinar = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number; input: UpdateWebinarInput },
  { rejectValue: AppError }
>('webinar/updateWebinar', async ({ workspaceId, id, input }, { rejectWithValue }) => {
  try {
    await webinarService.update(workspaceId, id, input);
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const importBannedWordsFromCsv = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number; file: File },
  { rejectValue: AppError }
>('webinar/importBannedWordsFromCsv', async ({ workspaceId, id, file }, { rejectWithValue }) => {
  try {
    await webinarService.importBannedWordsFromCsv(workspaceId, id, file);
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const addBannedWords = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number; words: string[] },
  { rejectValue: AppError }
>('webinar/addBannedWords', async ({ workspaceId, id, words }, { rejectWithValue }) => {
  try {
    await webinarService.addBannedWords(workspaceId, id, words);
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const removeBannedWords = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number; words: string[] },
  { rejectValue: AppError }
>('webinar/removeBannedWords', async ({ workspaceId, id, words }, { rejectWithValue }) => {
  try {
    await webinarService.removeBannedWords(workspaceId, id, words);
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

// Garde-fou du long polling : durée max d'attente avant d'abandonner (couvre le cas
// "le clone reste bloqué en INITIALIZATION"), et intervalle plancher entre deux appels
// pour ne pas marteler le back si jamais l'endpoint répond instantanément.
const DUPLICATION_POLL_MAX_DURATION_MS = 5 * 60 * 1000;
const DUPLICATION_POLL_MIN_INTERVAL_MS = 1000;

const wait = (durationMs: number) => new Promise((resolve) => setTimeout(resolve, durationMs));

export const cloneWebinar = createAsyncThunk<
  number,
  {
    workspaceId: number;
    sourceWebinarId: number;
    newTitle: string;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/cloneWebinar',
  async ({ workspaceId, sourceWebinarId, newTitle }, { rejectWithValue, dispatch }) => {
    if (!sourceWebinarId || !workspaceId || !newTitle.trim()) {
      return rejectWithValue({
        code: 'VALIDATION',
        message: 'errors.validation',
      });
    }

    try {
      const usage = await billingService.getUsage(workspaceId);
      const maxLiveRoles = getLiveRolesMax(usage);
      if (maxLiveRoles != null) {
        const sourcePresenters = await webinarService.listPresenters(workspaceId, sourceWebinarId);

        let sourceGuestsCount = 0;
        let guestPage = 0;
        const guestPageSize = 15;
        let hasMoreGuests = true;
        while (hasMoreGuests) {
          const page = await webinarService.listSpeakers(
            workspaceId,
            sourceWebinarId,
            guestPage,
            guestPageSize,
          );
          sourceGuestsCount += page.content.length;
          hasMoreGuests = page.content.length === guestPageSize;
          guestPage++;
        }

        if (wouldExceedLiveRolesLimit(sourcePresenters.length, sourceGuestsCount, maxLiveRoles)) {
          return rejectWithValue({
            code: 'LIVE_ROLES_LIMIT_EXCEEDED',
            message: 'errors.duplicationLiveRolesExceeded',
          });
        }
      }

      const initialResult = await webinarService.duplicate(workspaceId, {
        sourceWebinarId,
        newTitle,
      });

      if (!initialResult) {
        return rejectWithValue({
          code: 'WEBINAR_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      const { webinarId } = initialResult;
      let status = initialResult.status;

      // On attend que la copie des assets soit finie côté back avant de rendre la main.
      const deadline = Date.now() + DUPLICATION_POLL_MAX_DURATION_MS;
      while (status === 'INITIALIZATION') {
        if (Date.now() >= deadline) {
          return rejectWithValue({
            code: 'DUPLICATION_TIMEOUT',
            message: 'sessionPage.clone.timeout',
          });
        }

        const pollStartedAt = Date.now();
        const polled = await webinarService.getDuplicationStatus(workspaceId, webinarId);
        status = polled.status;

        // Le back bloque normalement ~25s par appel ; s'il a répondu vite en INITIALIZATION,
        // on temporise pour garder un rythme raisonnable.
        if (status === 'INITIALIZATION') {
          const elapsed = Date.now() - pollStartedAt;
          if (elapsed < DUPLICATION_POLL_MIN_INTERVAL_MS) {
            await wait(DUPLICATION_POLL_MIN_INTERVAL_MS - elapsed);
          }
        }
      }

      if (status === 'DUPLICATION_FAILED') {
        return rejectWithValue({
          code: 'DUPLICATION_FAILED',
          message: 'sessionPage.clone.failed',
        });
      }

      dispatch(fetchWebinars({ workspaceId }));
      return webinarId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWebinar = createAsyncThunk<
  number,
  { workspaceId: number; id: number },
  { rejectValue: AppError }
>('webinar/deleteWebinar', async ({ workspaceId, id }, { rejectWithValue }) => {
  try {
    await webinarService.delete(workspaceId, id);
    return id;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const unpublishWebinar = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number },
  { rejectValue: AppError }
>('webinar/unpublishWebinar', async ({ workspaceId, id }, { rejectWithValue }) => {
  try {
    await webinarService.unpublish(workspaceId, id);
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const publishWebinar = createAsyncThunk<
  Webinar,
  { workspaceId: number; id: number },
  { rejectValue: AppError }
>('webinar/publishWebinar', async ({ workspaceId, id }, { rejectWithValue }) => {
  try {
    await webinarService.publish(workspaceId, id);
    const webinar = await webinarService.getById(workspaceId, id);
    return webinar;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

// Speaker API
export const listSpeakers = createAsyncThunk<
  Speaker[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>('webinar/listSpeakers', async ({ workspaceId, webinarId, page, size }, { rejectWithValue }) => {
  try {
    return await listConfigurationEntries(
      (currentPage, pageSize) =>
        webinarService.listSpeakers(workspaceId, webinarId, currentPage, pageSize),
      page,
      size,
    );
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchSpeakerById = createAsyncThunk<
  Speaker,
  { workspaceId: number; webinarId: number; speakerId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchSpeakerById',
  async ({ workspaceId, webinarId, speakerId }, { rejectWithValue }) => {
    try {
      const speaker = await webinarService.getSpeaker(workspaceId, webinarId, speakerId);
      return speaker;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createSpeaker = createAsyncThunk<
  number,
  {
    workspaceId: number;
    webinarId: number;
    input: SpeakerInput;
    file?: File | null;
    skipListSpeakers?: boolean;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createSpeaker',
  async (
    { workspaceId, webinarId, input, file, skipListSpeakers },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const speakerId = await webinarService.createSpeaker(workspaceId, webinarId, input, file);
      if (!speakerId) {
        return rejectWithValue({
          code: 'SPEAKER_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      if (!skipListSpeakers) {
        dispatch(listSpeakers({ workspaceId, webinarId }));
      }
      return speakerId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateSpeaker = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    speakerId: number;
    input: SpeakerInput;
    skipListSpeakers?: boolean;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateSpeaker',
  async (
    { workspaceId, webinarId, speakerId, input, skipListSpeakers },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.updateSpeaker(workspaceId, webinarId, speakerId, input);
      if (!skipListSpeakers) {
        dispatch(listSpeakers({ workspaceId, webinarId }));
      }
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteSpeaker = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; speakerId: number; skipListSpeakers?: boolean },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteSpeaker',
  async (
    { workspaceId, webinarId, speakerId, skipListSpeakers },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.deleteSpeaker(workspaceId, webinarId, speakerId);
      if (!skipListSpeakers) {
        dispatch(listSpeakers({ workspaceId, webinarId }));
      }
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadSpeakerPicture = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    speakerId: number;
    file: File;
    skipListSpeakers?: boolean;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/uploadSpeakerPicture',
  async (
    { workspaceId, webinarId, speakerId, file, skipListSpeakers },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.uploadSpeakerPicture(workspaceId, webinarId, speakerId, file);
      if (!skipListSpeakers) {
        dispatch(listSpeakers({ workspaceId, webinarId }));
      }
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteSpeakerPicture = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; speakerId: number; skipListSpeakers?: boolean },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteSpeakerPicture',
  async (
    { workspaceId, webinarId, speakerId, skipListSpeakers },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.deleteSpeakerPicture(workspaceId, webinarId, speakerId);
      if (!skipListSpeakers) {
        dispatch(listSpeakers({ workspaceId, webinarId }));
      }
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Moderator API
export const listModerators = createAsyncThunk<
  Moderator[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>('webinar/listModerators', async ({ workspaceId, webinarId, page, size }, { rejectWithValue }) => {
  try {
    return await listConfigurationEntries(
      (currentPage, pageSize) =>
        webinarService.listModerators(workspaceId, webinarId, currentPage, pageSize),
      page,
      size,
    );
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchModeratorById = createAsyncThunk<
  Moderator,
  { workspaceId: number; webinarId: number; moderatorId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchModeratorById',
  async ({ workspaceId, webinarId, moderatorId }, { rejectWithValue }) => {
    try {
      const moderator = await webinarService.getModerator(workspaceId, webinarId, moderatorId);
      return moderator;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createModerator = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: ModeratorInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createModerator',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      const moderatorId = await webinarService.createModerator(workspaceId, webinarId, input);
      if (!moderatorId) {
        return rejectWithValue({
          code: 'MODERATOR_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      dispatch(listModerators({ workspaceId, webinarId }));
      return moderatorId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateModerator = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; moderatorId: number; input: ModeratorInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateModerator',
  async ({ workspaceId, webinarId, moderatorId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.updateModerator(workspaceId, webinarId, moderatorId, input);
      dispatch(listModerators({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteModerator = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; moderatorId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteModerator',
  async ({ workspaceId, webinarId, moderatorId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteModerator(workspaceId, webinarId, moderatorId);
      dispatch(listModerators({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Presenter API (POST, GET, PUT, DELETE, LIST)
export const listPresenters = createAsyncThunk<
  Presenter[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/listPresenters', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const presenters = await webinarService.listPresenters(workspaceId, webinarId);
    return presenters;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const createPresenter = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: PresenterInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createPresenter',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      const presenterId = await webinarService.createPresenter(workspaceId, webinarId, input);
      if (!presenterId) {
        return rejectWithValue({
          code: 'PRESENTER_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      dispatch(listPresenters({ workspaceId, webinarId }));
      return presenterId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPresenterById = createAsyncThunk<
  Presenter,
  { workspaceId: number; webinarId: number; presenterId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchPresenterById',
  async ({ workspaceId, webinarId, presenterId }, { rejectWithValue }) => {
    try {
      const presenter = await webinarService.getPresenter(workspaceId, webinarId, presenterId);
      return presenter;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updatePresenter = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    presenterId: number;
    input: PresenterInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updatePresenter',
  async ({ workspaceId, webinarId, presenterId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.updatePresenter(workspaceId, webinarId, presenterId, input);
      dispatch(fetchPresenterById({ workspaceId, webinarId, presenterId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deletePresenter = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; presenterId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deletePresenter',
  async ({ workspaceId, webinarId, presenterId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deletePresenter(workspaceId, webinarId, presenterId);
      dispatch(listPresenters({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWebinarSessionConfiguration = createAsyncThunk<
  WebinarSessionConfiguration,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchWebinarSessionConfiguration',
  async ({ workspaceId, webinarId }, { rejectWithValue }) => {
    try {
      const configuration = await webinarService.getWebinarSession(workspaceId, webinarId);
      return configuration;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicWebinarSession = createAsyncThunk<
  PublicWebinarSession,
  { webinarId: number; sessionIdentifier: string },
  { rejectValue: AppError }
>(
  'webinar/fetchPublicWebinarSession',
  async ({ webinarId, sessionIdentifier }, { rejectWithValue }) => {
    try {
      const session = await readPublicSessionShared(webinarId, sessionIdentifier, () =>
        webinarService.getPublicSession(webinarId, sessionIdentifier),
      );
      return session;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
          status: err.status,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
        status: e.status,
      });
    }
  },
);

export const fetchPublicWebinarSubscriptionForm = createAsyncThunk<
  PublicWebinarSubscriptionForm,
  { webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchPublicWebinarSubscriptionForm', async ({ webinarId }, { rejectWithValue }) => {
  try {
    const form = await webinarService.getPublicSubscriptionForm(webinarId);
    return form;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicWebinarIndex = createAsyncThunk<
  WebinarIndex,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>('webinar/fetchPublicWebinarIndex', async ({ webinarId, authorization }, { rejectWithValue }) => {
  try {
    const index = await webinarService.getPublicIndex(webinarId, authorization);
    return index;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicWebinarMe = createAsyncThunk<
  PublicWebinarMe,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>('webinar/fetchPublicWebinarMe', async ({ webinarId, authorization }, { rejectWithValue }) => {
  try {
    const me = await webinarService.getPublicWebinarMe(webinarId, authorization);
    return me;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchPublicWaitingRoomSurvey = createAsyncThunk<
  SurveyTemplate,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchPublicWaitingRoomSurvey',
  async ({ webinarId, authorization }, { rejectWithValue }) => {
    try {
      const survey = await webinarService.getPublicWaitingRoomSurvey(webinarId, authorization);
      return survey;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchPublicWaitingRoomQuiz = createAsyncThunk<
  QuizTemplate,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchPublicWaitingRoomQuiz',
  async ({ webinarId, authorization }, { rejectWithValue }) => {
    try {
      const quiz = await webinarService.getPublicWaitingRoomQuiz(webinarId, authorization);
      return quiz;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const validatePublicSessionCode = createAsyncThunk<
  void,
  { webinarId: number; sessionIdentifier: string; token: string; code: string },
  { rejectValue: AppError }
>(
  'webinar/validatePublicSessionCode',
  async ({ webinarId, sessionIdentifier, token, code }, { rejectWithValue }) => {
    try {
      await webinarService.validatePublicSessionCode(webinarId, sessionIdentifier, token, code);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicWaitingRoomSurvey = createAsyncThunk<
  void,
  {
    webinarId: number;
    data: {
      questions: Array<{
        question: string;
        answers: string[];
      }>;
    };
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'webinar/submitPublicWaitingRoomSurvey',
  async ({ webinarId, data, authorization }, { rejectWithValue }) => {
    try {
      await webinarService.submitPublicWaitingRoomSurvey(webinarId, data, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);
export const fetchPublicWaitingRoomPage = createAsyncThunk<
  WaitingRoomPage,
  { webinarId: number; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchPublicWaitingRoomPage',
  async ({ webinarId, authorization }, { rejectWithValue }) => {
    try {
      const waitingRoomPage = await webinarService.getPublicWaitingRoomPage(
        webinarId,
        authorization,
      );
      return waitingRoomPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const registerPublicWebinar = createAsyncThunk<
  WebinarSubscriptionDto,
  {
    data: {
      sessionIdentifier: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phoneNumber?: string;
    };
  },
  { rejectValue: AppError }
>('webinar/registerPublicWebinar', async ({ data }, { rejectWithValue }) => {
  try {
    const response = await webinarService.registerPublicWebinar(data);
    return response;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const registerPublicWebinarReplay = createAsyncThunk<
  WebinarSubscriptionDto,
  {
    webinarId: number;
    sessionIdentifier: string;
    data: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phoneNumber?: string;
    };
  },
  { rejectValue: AppError }
>(
  'webinar/registerPublicWebinarReplay',
  async ({ webinarId, sessionIdentifier, data }, { rejectWithValue }) => {
    try {
      const response = await webinarService.registerPublicWebinarReplay(
        webinarId,
        sessionIdentifier,
        data,
      );
      return response;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicSurvey = createAsyncThunk<
  void,
  {
    webinarId: number;
    templateId: number;
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string;
      }>;
    };
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'webinar/submitPublicSurvey',
  async ({ webinarId, templateId, data, authorization }, { rejectWithValue }) => {
    try {
      await webinarService.submitPublicSurvey(webinarId, templateId, data, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicSurveyPut = createAsyncThunk<
  void,
  {
    webinarId: number;
    templateId: number;
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
        freeText: string;
      }>;
    };
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'webinar/submitPublicSurveyPut',
  async ({ webinarId, templateId, data, authorization }, { rejectWithValue }) => {
    try {
      await webinarService.submitPublicSurveyPut(webinarId, templateId, data, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicQuiz = createAsyncThunk<
  void,
  {
    webinarId: number;
    templateId: number;
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    };
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'webinar/submitPublicQuiz',
  async ({ webinarId, templateId, data, authorization }, { rejectWithValue }) => {
    try {
      await webinarService.submitPublicQuiz(webinarId, templateId, data, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const submitPublicQuizPut = createAsyncThunk<
  void,
  {
    webinarId: number;
    templateId: number;
    data: {
      sessionId: number;
      answers: Array<{
        questionId: number;
        optionIds: number[];
      }>;
    };
    authorization: string;
  },
  { rejectValue: AppError }
>(
  'webinar/submitPublicQuizPut',
  async ({ webinarId, templateId, data, authorization }, { rejectWithValue }) => {
    try {
      await webinarService.submitPublicQuizPut(webinarId, templateId, data, authorization);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchMyPublicSurveySubmission = createAsyncThunk<
  PublicSurveySubmission | null,
  { webinarId: number; templateId: number; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchMyPublicSurveySubmission',
  async ({ webinarId, templateId, authorization }, { rejectWithValue }) => {
    try {
      const response = await webinarService.getMyPublicSurveySubmission(
        webinarId,
        templateId,
        authorization,
      );
      return response;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchMyPublicQuizSubmission = createAsyncThunk<
  QuizSubmission | null,
  { webinarId: number; templateId: number; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchMyPublicQuizSubmission',
  async ({ webinarId, templateId, authorization }, { rejectWithValue }) => {
    try {
      const response = await webinarService.getMyPublicQuizSubmission(
        webinarId,
        templateId,
        authorization,
      );
      return response;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listWebinarSessions = createAsyncThunk<
  WebinarSession[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listWebinarSessions',
  async ({ workspaceId, webinarId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const response = await webinarService.listSessions(workspaceId, webinarId, page, size);
      return response.content;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listSessionAttendees = createAsyncThunk<
  { content: WebinarSessionAttendee[]; totalElements: number; totalPages: number },
  {
    workspaceId: number;
    webinarId: number;
    sessionId: number;
    page?: number;
    size?: number;
    search?: string;
  },
  { rejectValue: AppError }
>(
  'webinar/listSessionAttendees',
  async (
    { workspaceId, webinarId, sessionId, page = 0, size = 10, search },
    { rejectWithValue },
  ) => {
    try {
      const response = await webinarService.listSessionAttendees(
        workspaceId,
        webinarId,
        sessionId,
        page,
        size,
        search,
      );
      return {
        content: response.content,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      };
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

/** Same pattern as analytics `exportSessionProspects`: no Blob in Redux actions */
function triggerAttendeesBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function sessionAttendeesExportFilename(sessionId: number, blob: Blob): string {
  if (blob.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return `inscrits-session-${sessionId}.xlsx`;
  }
  if (blob.type === 'text/csv' || blob.type === 'application/csv') {
    return `inscrits-session-${sessionId}.csv`;
  }
  return `inscrits-session-${sessionId}`;
}

export const exportSessionAttendees = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; sessionId: number },
  { rejectValue: string }
>(
  'webinar/exportSessionAttendees',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      const blob = await webinarService.getSessionAttendeesExport(
        workspaceId,
        webinarId,
        sessionId,
      );

      if (blob.size === 0) {
        return rejectWithValue('EMPTY_SESSION_ATTENDEES_EXPORT');
      }

      // Erreur API renvoyée en JSON mais lue en blob (corps non vide)
      if (blob.type.includes('json')) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text) as { message?: string };
          return rejectWithValue(parsed.message ?? 'FAILED_TO_EXPORT_SESSION_ATTENDEES');
        } catch {
          return rejectWithValue('FAILED_TO_EXPORT_SESSION_ATTENDEES');
        }
      }

      triggerAttendeesBlobDownload(blob, sessionAttendeesExportFilename(sessionId, blob));
    } catch (err) {
      return rejectWithValue((err as Error).message || 'FAILED_TO_EXPORT_SESSION_ATTENDEES');
    }
  },
);

// Le back stocke le CSV et le traite en tâche de fond (endpoint /import-async) pour ne plus bloquer
// la requête HTTP sur les gros fichiers ; on poll le statut jusqu'à COMPLETED/FAILED.
const ATTENDEE_IMPORT_POLL_MAX_DURATION_MS = 10 * 60 * 1000;
const ATTENDEE_IMPORT_POLL_INTERVAL_MS = 2000;

export const importSessionAttendees = createAsyncThunk<
  SessionAttendeesImportResult,
  { workspaceId: number; webinarId: number; sessionId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/importSessionAttendees',
  async ({ workspaceId, webinarId, sessionId, file }, { rejectWithValue }) => {
    try {
      let status: SessionAttendeesImportStatus =
        await webinarService.initiateSessionAttendeesImport(
          workspaceId,
          webinarId,
          sessionId,
          file,
        );

      const deadline = Date.now() + ATTENDEE_IMPORT_POLL_MAX_DURATION_MS;
      while (status.status === 'QUEUED' || status.status === 'PROCESSING') {
        if (Date.now() >= deadline) {
          return rejectWithValue({
            code: 'ATTENDEE_IMPORT_TIMEOUT',
            message: 'errors.attendeeImportTimeout',
          });
        }
        await wait(ATTENDEE_IMPORT_POLL_INTERVAL_MS);
        status = await webinarService.getSessionAttendeesImportStatus(
          workspaceId,
          webinarId,
          sessionId,
          status.importId,
        );
      }

      if (status.status === 'FAILED') {
        const errorCode = status.errorCode ?? 'HTTP.ERROR.500.ATTENDEE.CSV.IMPORT.FAILED';
        return rejectWithValue({ code: errorCode, message: mapErrorMessage(errorCode) });
      }

      return {
        createdCount: status.createdCount,
        updatedCount: status.updatedCount,
        unchangedCount: status.unchangedCount,
        duplicateRowsCount: status.duplicateRowsCount,
      };
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);
        return rejectWithValue({ code: err.code, message: messageKey, details: err.details });
      }
      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);
      return rejectWithValue({ code: e.code ?? 'UNKNOWN', message: messageKey });
    }
  },
);

export const deleteSessionAttendee = createAsyncThunk<
  { subscriptionId: number },
  { workspaceId: number; webinarId: number; sessionId: number; subscriptionId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteSessionAttendee',
  async ({ workspaceId, webinarId, sessionId, subscriptionId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteSessionAttendee(workspaceId, webinarId, sessionId, subscriptionId);
      return { subscriptionId };
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listWorkspaceAccesses = createAsyncThunk<
  WorkspaceTeamMember[],
  { workspaceId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listWorkspaceAccesses',
  async ({ workspaceId, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      const response = await webinarService.getWorkspaceAccesses(workspaceId, page, size);
      return response.content;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createWorkspaceInvitation = createAsyncThunk<
  WorkspaceTeamInvitation,
  { workspaceId: number; input: WorkspaceTeamInviteInput; file?: File | null },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createWorkspaceInvitation',
  async ({ workspaceId, input, file }, { rejectWithValue, dispatch }) => {
    try {
      const invitation = await webinarService.createWorkspaceInvitation(workspaceId, input, file);
      dispatch(listWorkspaceAccesses({ workspaceId }));
      return invitation;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const getWebinarSession = createAsyncThunk<
  WebinarSession,
  { workspaceId: number; webinarId: number; sessionId: number },
  { rejectValue: AppError }
>(
  'webinar/getWebinarSession',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await webinarService.getSession(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWebinarSessionDetail = createAsyncThunk<
  WebinarSessionDetail,
  { workspaceId: number; webinarId: number; sessionId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchWebinarSessionDetail',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await webinarService.getSessionDetail(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchSessionChatExport = createAsyncThunk<
  Blob,
  { workspaceId: number; webinarId: number; sessionId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchSessionChatExport',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      return await webinarService.getSessionChatExport(workspaceId, webinarId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateWebinarSessionConfiguration = createAsyncThunk<
  WebinarSessionConfiguration,
  { workspaceId: number; webinarId: number; input: WebinarSessionConfigurationInput },
  { rejectValue: AppError }
>(
  'webinar/updateWebinarSessionConfiguration',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateSessionConfiguration(workspaceId, webinarId, input);
      const configuration = await webinarService.getWebinarSession(workspaceId, webinarId);
      return configuration;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWebinarSession = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; sessionId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteWebinarSession',
  async ({ workspaceId, webinarId, sessionId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteSession(workspaceId, webinarId, sessionId);
      return sessionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Webinar Index Thunks
export const fetchWebinarIndex = createAsyncThunk<
  WebinarIndex,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchWebinarIndex', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const index = await webinarService.getIndex(workspaceId, webinarId);
    return index;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const listIndexItems = createAsyncThunk<
  IndexItem[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/listIndexItems', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const items = await webinarService.listIndexItems(workspaceId, webinarId);
    return items;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const createIndexItem = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; input: IndexItemInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createIndexItem',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.createIndexItem(workspaceId, webinarId, input);
      dispatch(fetchWebinarIndex({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateIndexItem = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; itemId: number; input: IndexItemInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateIndexItem',
  async ({ workspaceId, webinarId, itemId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.updateIndexItem(workspaceId, webinarId, itemId, input);
      dispatch(fetchWebinarIndex({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteIndexItem = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; itemId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteIndexItem',
  async ({ workspaceId, webinarId, itemId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteIndexItem(workspaceId, webinarId, itemId);
      dispatch(fetchWebinarIndex({ workspaceId, webinarId }));
      return itemId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const replaceIndexItems = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; input: IndexItemBulkReplaceInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/replaceIndexItems',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.replaceIndexItems(workspaceId, webinarId, input);
      dispatch(fetchWebinarIndex({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWebinarSubscriptionPage = createAsyncThunk<
  WebinarSubscriptionPage,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchWebinarSubscriptionPage',
  async ({ workspaceId, webinarId }, { rejectWithValue }) => {
    try {
      const subscriptionPage = await webinarService.getSubscriptionPage(workspaceId, webinarId);
      return subscriptionPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateWebinarSubscriptionPage = createAsyncThunk<
  WebinarSubscriptionPage,
  { workspaceId: number; webinarId: number; input: WebinarSubscriptionPageInput },
  { rejectValue: AppError }
>(
  'webinar/updateWebinarSubscriptionPage',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateSubscriptionPage(workspaceId, webinarId, input);
      const subscriptionPage = await webinarService.getSubscriptionPage(workspaceId, webinarId);
      return subscriptionPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadWebinarSubscriptionPageBackgroundImage = createAsyncThunk<
  WebinarSubscriptionPage,
  { workspaceId: number; webinarId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/uploadWebinarSubscriptionPageBackgroundImage',
  async ({ workspaceId, webinarId, file }, { rejectWithValue }) => {
    try {
      await webinarService.uploadBackgroundImage(workspaceId, webinarId, file);
      const subscriptionPage = await webinarService.getSubscriptionPage(workspaceId, webinarId);
      return subscriptionPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchReplayConfiguration = createAsyncThunk<
  ReplayConfiguration,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchReplayConfiguration', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const replayConfiguration = await webinarService.getReplayConfiguration(workspaceId, webinarId);
    return replayConfiguration;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const saveEvergreenSources = createAsyncThunk<
  EvergreenConversionOutcome | null,
  {
    workspaceId: number;
    webinarId: number;
    input: EvergreenSourcesInput;
    // Interactions reprises d'un Live : attendre la fin de leur récupération.
    awaitConversion?: boolean;
  },
  { rejectValue: AppError }
>(
  'webinar/saveEvergreenSources',
  async ({ workspaceId, webinarId, input, awaitConversion = false }, { rejectWithValue }) => {
    try {
      await retryWhileSourceTimelinePreparing(() =>
        webinarService.updateEvergreenSources(workspaceId, webinarId, input),
      );
      if (!awaitConversion) return null;
      return await waitForEvergreenConversion({
        loadConfiguration: () => webinarService.getEvergreenConfiguration(workspaceId, webinarId),
      });
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
          apiCode: resolveApiErrorCode(err) ?? undefined,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
        apiCode: resolveApiErrorCode(err) ?? undefined,
      });
    }
  },
);

export const importEvergreenChat = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; file: File },
  { rejectValue: AppError }
>('webinar/importEvergreenChat', async ({ workspaceId, webinarId, file }, { rejectWithValue }) => {
  try {
    await webinarService.importEvergreenChat(workspaceId, webinarId, file);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchEvergreenChatConfiguration = createAsyncThunk<
  EvergreenChatConfiguration,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchEvergreenChatConfiguration',
  async ({ workspaceId, webinarId }, { rejectWithValue }) => {
    try {
      const config = await webinarService.getEvergreenChatConfiguration(workspaceId, webinarId);
      return config;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateEvergreenChatConfiguration = createAsyncThunk<
  EvergreenChatConfiguration,
  {
    workspaceId: number;
    webinarId: number;
    input: UpdateEvergreenChatConfigInput;
  },
  { rejectValue: AppError }
>(
  'webinar/updateEvergreenChatConfiguration',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateEvergreenChatConfiguration(workspaceId, webinarId, input);
      const config = await webinarService.getEvergreenChatConfiguration(workspaceId, webinarId);
      return config;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const addEvergreenChatMessage = createAsyncThunk<
  EvergreenChatConfiguration,
  {
    workspaceId: number;
    webinarId: number;
    input: AddManualChatMessageInput;
  },
  { rejectValue: AppError }
>(
  'webinar/addEvergreenChatMessage',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.addEvergreenChatMessage(workspaceId, webinarId, input);
      const config = await webinarService.getEvergreenChatConfiguration(workspaceId, webinarId);
      return config;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateEvergreenChatMessage = createAsyncThunk<
  EvergreenChatConfiguration,
  {
    workspaceId: number;
    webinarId: number;
    messageId: number;
    input: AddManualChatMessageInput;
  },
  { rejectValue: AppError }
>(
  'webinar/updateEvergreenChatMessage',
  async ({ workspaceId, webinarId, messageId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateEvergreenChatMessage(workspaceId, webinarId, messageId, input);
      const config = await webinarService.getEvergreenChatConfiguration(workspaceId, webinarId);
      return config;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteEvergreenChatMessages = createAsyncThunk<
  EvergreenChatConfiguration,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteEvergreenChatMessages',
  async ({ workspaceId, webinarId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteEvergreenChatMessages(workspaceId, webinarId);
      const config = await webinarService.getEvergreenChatConfiguration(workspaceId, webinarId);
      return config;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchEvergreenConfiguration = createAsyncThunk<
  EvergreenConfiguration,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchEvergreenConfiguration',
  async ({ workspaceId, webinarId }, { rejectWithValue }) => {
    try {
      const evergreenConfiguration = await webinarService.getEvergreenConfiguration(
        workspaceId,
        webinarId,
      );
      return evergreenConfiguration;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchEvergreenTimeline = createAsyncThunk<
  EvergreenTimelineEvent[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchEvergreenTimeline', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const timeline = await webinarService.getEvergreenTimeline(workspaceId, webinarId);
    return timeline;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const replaceEvergreenTimeline = createAsyncThunk<
  EvergreenTimelineEvent[],
  {
    workspaceId: number;
    webinarId: number;
    input: EvergreenTimelineEventInput[];
  },
  { rejectValue: AppError }
>(
  'webinar/replaceEvergreenTimeline',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      const timeline = await webinarService.replaceEvergreenTimeline(workspaceId, webinarId, input);
      return timeline;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

function toScheduledInteractionError(err: unknown): AppError {
  const e = err as { code?: string; message?: string; status?: number; details?: unknown };
  return {
    code: e.code ?? 'UNKNOWN',
    message: mapErrorMessage(e.message, e.code, e.status),
    status: e.status,
    details: e.details,
    apiCode: resolveApiErrorCode(err) ?? undefined,
  };
}

export const saveEvergreenScheduledInteraction = createAsyncThunk<
  EvergreenScheduledInteraction,
  {
    workspaceId: number;
    webinarId: number;
    interactionId?: number;
    input: EvergreenScheduledInteractionInput;
  },
  { rejectValue: AppError }
>(
  'webinar/saveEvergreenScheduledInteraction',
  async ({ workspaceId, webinarId, interactionId, input }, { rejectWithValue, dispatch }) => {
    try {
      const saved =
        interactionId == null
          ? await webinarService.createEvergreenScheduledInteraction(workspaceId, webinarId, input)
          : await webinarService.updateEvergreenScheduledInteraction(
              workspaceId,
              webinarId,
              interactionId,
              input,
            );
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
      return saved;
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const deleteEvergreenScheduledInteraction = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; interactionId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteEvergreenScheduledInteraction',
  async ({ workspaceId, webinarId, interactionId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteEvergreenScheduledInteraction(
        workspaceId,
        webinarId,
        interactionId,
      );
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const moveEvergreenPurchaseNotification = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; eventId: number; startOffsetSeconds: number },
  { rejectValue: AppError }
>(
  'webinar/moveEvergreenPurchaseNotification',
  async (
    { workspaceId, webinarId, eventId, startOffsetSeconds },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.moveEvergreenPurchaseNotification(
        workspaceId,
        webinarId,
        eventId,
        startOffsetSeconds,
      );
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const deleteEvergreenPurchaseNotification = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; eventId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteEvergreenPurchaseNotification',
  async ({ workspaceId, webinarId, eventId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteEvergreenTimeline(workspaceId, webinarId, [eventId]);
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const createEvergreenManualPurchaseNotification = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; input: EvergreenManualPurchaseNotificationInput },
  { rejectValue: AppError }
>(
  'webinar/createEvergreenManualPurchaseNotification',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.createEvergreenManualPurchaseNotification(workspaceId, webinarId, input);
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const updateEvergreenManualPurchaseNotification = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    eventId: number;
    input: EvergreenManualPurchaseNotificationInput;
  },
  { rejectValue: AppError }
>(
  'webinar/updateEvergreenManualPurchaseNotification',
  async ({ workspaceId, webinarId, eventId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.updateEvergreenManualPurchaseNotification(
        workspaceId,
        webinarId,
        eventId,
        input,
      );
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const deleteEvergreenManualPurchaseNotification = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; eventId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteEvergreenManualPurchaseNotification',
  async ({ workspaceId, webinarId, eventId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteEvergreenManualPurchaseNotification(
        workspaceId,
        webinarId,
        eventId,
      );
      await dispatch(fetchEvergreenTimeline({ workspaceId, webinarId }));
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId }));
    } catch (err) {
      return rejectWithValue(toScheduledInteractionError(err));
    }
  },
);

export const deleteEvergreenTimeline = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; ids: number[] },
  { rejectValue: AppError }
>(
  'webinar/deleteEvergreenTimeline',
  async ({ workspaceId, webinarId, ids }, { rejectWithValue }) => {
    try {
      await webinarService.deleteEvergreenTimeline(workspaceId, webinarId, ids);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey = mapErrorMessage(err.message, err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey = mapErrorMessage(e.message, e.code, e.status);

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateEvergreenConfiguration = createAsyncThunk<
  EvergreenConfiguration,
  {
    workspaceId: number;
    webinarId: number;
    input: EvergreenConfigurationInput;
  },
  { rejectValue: AppError }
>(
  'webinar/updateEvergreenConfiguration',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateEvergreenConfiguration(workspaceId, webinarId, input);
      const evergreenConfiguration = await webinarService.getEvergreenConfiguration(
        workspaceId,
        webinarId,
      );
      return evergreenConfiguration;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateReplayConfiguration = createAsyncThunk<
  ReplayConfiguration,
  { workspaceId: number; webinarId: number; input: ReplayConfigurationInput },
  { rejectValue: AppError }
>(
  'webinar/updateReplayConfiguration',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateReplayConfiguration(workspaceId, webinarId, input);
      const replayConfiguration = await webinarService.getReplayConfiguration(
        workspaceId,
        webinarId,
      );
      return replayConfiguration;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Waiting Room Page Thunks
export const fetchWaitingRoomPage = createAsyncThunk<
  WaitingRoomPage,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchWaitingRoomPage', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const waitingRoomPage = await webinarService.getWaitingRoomPage(workspaceId, webinarId);
    return waitingRoomPage;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const updateWaitingRoomPage = createAsyncThunk<
  WaitingRoomPage,
  { workspaceId: number; webinarId: number; input: WaitingRoomPageInput },
  { rejectValue: AppError }
>(
  'webinar/updateWaitingRoomPage',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateWaitingRoomPage(workspaceId, webinarId, input);
      const waitingRoomPage = await webinarService.getWaitingRoomPage(workspaceId, webinarId);
      if (input.displayMainMedia !== undefined) {
        waitingRoomPage.displayMainMedia = input.displayMainMedia;
      }
      return waitingRoomPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadWaitingRoomPageBackgroundImage = createAsyncThunk<
  WaitingRoomPage,
  { workspaceId: number; webinarId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/uploadWaitingRoomPageBackgroundImage',
  async ({ workspaceId, webinarId, file }, { rejectWithValue }) => {
    try {
      await webinarService.uploadWaitingRoomPageBackgroundImage(workspaceId, webinarId, file);
      const waitingRoomPage = await webinarService.getWaitingRoomPage(workspaceId, webinarId);
      return waitingRoomPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadWaitingRoomPageImage = createAsyncThunk<
  WaitingRoomPage,
  { workspaceId: number; webinarId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/uploadWaitingRoomPageImage',
  async ({ workspaceId, webinarId, file }, { rejectWithValue }) => {
    try {
      await webinarService.uploadWaitingRoomPageImage(workspaceId, webinarId, file);
      const waitingRoomPage = await webinarService.getWaitingRoomPage(workspaceId, webinarId);
      return waitingRoomPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadWaitingRoomPageVideo = createAsyncThunk<
  WaitingRoomPage,
  { workspaceId: number; webinarId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/uploadWaitingRoomPageVideo',
  async ({ workspaceId, webinarId, file }, { rejectWithValue }) => {
    try {
      const uploadInit = await webinarService.initWaitingRoomPageVideoUpload(
        workspaceId,
        webinarId,
        {
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        },
      );

      const putResponse = await fetch(uploadInit.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
        body: file,
      });

      if (!putResponse.ok) {
        return rejectWithValue({
          code: 'UPLOAD_FAILED',
          message: 'errors.generic',
        });
      }

      await webinarService.finalizeWaitingRoomPageVideoUpload(workspaceId, webinarId, {
        resourceKey: uploadInit.resourceKey,
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        pageCount: null,
        checksumSha256: null,
        position: null,
      });

      const waitingRoomPage = await webinarService.getWaitingRoomPage(workspaceId, webinarId);
      return waitingRoomPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWaitingRoomPageMainAsset = createAsyncThunk<
  WaitingRoomPage,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteWaitingRoomPageMainAsset',
  async ({ workspaceId, webinarId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteWaitingRoomPageMainAsset(workspaceId, webinarId);
      return await webinarService.getWaitingRoomPage(workspaceId, webinarId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Thank You Page Thunks
export const fetchThankYouPage = createAsyncThunk<
  ThankYouPage,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchThankYouPage', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const thankYouPage = await webinarService.getThankYouPage(workspaceId, webinarId);
    return thankYouPage;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const updateThankYouPage = createAsyncThunk<
  ThankYouPage,
  { workspaceId: number; webinarId: number; input: ThankYouPageInput },
  { rejectValue: AppError }
>('webinar/updateThankYouPage', async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
  try {
    await webinarService.updateThankYouPage(workspaceId, webinarId, input);
    const thankYouPage = await webinarService.getThankYouPage(workspaceId, webinarId);
    return thankYouPage;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const uploadThankYouPageImage = createAsyncThunk<
  ThankYouPage,
  { workspaceId: number; webinarId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/uploadThankYouPageImage',
  async ({ workspaceId, webinarId, file }, { rejectWithValue }) => {
    try {
      await webinarService.uploadThankYouPageImage(workspaceId, webinarId, file);
      const thankYouPage = await webinarService.getThankYouPage(workspaceId, webinarId);
      return thankYouPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteThankYouPageImage = createAsyncThunk<
  ThankYouPage,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/deleteThankYouPageImage', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    await webinarService.deleteThankYouPageImage(workspaceId, webinarId);
    const thankYouPage = await webinarService.getThankYouPage(workspaceId, webinarId);
    return thankYouPage;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const listOfferTemplates = createAsyncThunk<
  OfferTemplate[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listOfferTemplates',
  async ({ workspaceId, webinarId, page, size }, { rejectWithValue }) => {
    try {
      return await listConfigurationEntries(
        (currentPage, pageSize) =>
          webinarService.listOfferTemplates(workspaceId, webinarId, currentPage, pageSize),
        page,
        size,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchOfferTemplate = createAsyncThunk<
  OfferTemplate,
  { workspaceId: number; webinarId: number; offerId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchOfferTemplate',
  async ({ workspaceId, webinarId, offerId }, { rejectWithValue }) => {
    try {
      const offerTemplate = await webinarService.getOfferTemplate(workspaceId, webinarId, offerId);
      return offerTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createOfferTemplate = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: OfferTemplateInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>('webinar/createOfferTemplate', async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
  try {
    const createdOfferId = await webinarService.createOfferTemplate(workspaceId, webinarId, input);
    if (!createdOfferId) {
      return rejectWithValue({
        code: 'OFFER_TEMPLATE_ID_NOT_FOUND',
        message: 'errors.generic',
      });
    }

    // dispatch(listOfferTemplates({ workspaceId, webinarId }));
    return createdOfferId;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const updateOfferTemplate = createAsyncThunk<
  OfferTemplate,
  { workspaceId: number; webinarId: number; offerId: number; input: OfferTemplateInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateOfferTemplate',
  async ({ workspaceId, webinarId, offerId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.updateOfferTemplate(workspaceId, webinarId, offerId, input);
      const offerTemplate = await webinarService.getOfferTemplate(workspaceId, webinarId, offerId);
      dispatch(listOfferTemplates({ workspaceId, webinarId }));
      return offerTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteOfferTemplate = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; offerId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteOfferTemplate',
  async ({ workspaceId, webinarId, offerId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteOfferTemplate(workspaceId, webinarId, offerId);
      await dispatch(listOfferTemplates({ workspaceId, webinarId }));
      return offerId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const upsertOfferTemplateContent = createAsyncThunk<
  OfferTemplate,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    contentType: string;
    input: OfferTemplateContentInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/upsertOfferTemplateContent',
  async (
    { workspaceId, webinarId, templateId, contentType, input },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.upsertOfferTemplateContent(
        workspaceId,
        webinarId,
        templateId,
        contentType,
        input,
      );
      const offerTemplate = await webinarService.getOfferTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      dispatch(listOfferTemplates({ workspaceId, webinarId }));
      return offerTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteOfferTemplateContent = createAsyncThunk<
  OfferTemplate,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    contentType: string;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteOfferTemplateContent',
  async ({ workspaceId, webinarId, templateId, contentType }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteOfferTemplateContent(
        workspaceId,
        webinarId,
        templateId,
        contentType,
      );
      const offerTemplate = await webinarService.getOfferTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      dispatch(listOfferTemplates({ workspaceId, webinarId }));
      return offerTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteOfferTemplateContentImage = createAsyncThunk<
  OfferTemplate,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    contentType: string;
  },
  { rejectValue: AppError }
>(
  'webinar/deleteOfferTemplateContentImage',
  async ({ workspaceId, webinarId, templateId, contentType }, { rejectWithValue }) => {
    try {
      await webinarService.deleteOfferTemplateContentImage(
        workspaceId,
        webinarId,
        templateId,
        contentType,
      );
      const offerTemplate = await webinarService.getOfferTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      return offerTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadOfferTemplateContentImage = createAsyncThunk<
  OfferTemplate,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    contentType: string;
    file: File;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/uploadOfferTemplateContentImage',
  async (
    { workspaceId, webinarId, templateId, contentType, file },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.uploadOfferTemplateContentImage(
        workspaceId,
        webinarId,
        templateId,
        contentType,
        file,
      );
      const offerTemplate = await webinarService.getOfferTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      dispatch(listOfferTemplates({ workspaceId, webinarId }));
      return offerTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Survey Template Thunks
export const listSurveyTemplates = createAsyncThunk<
  SurveyTemplate[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listSurveyTemplates',
  async ({ workspaceId, webinarId, page, size }, { rejectWithValue }) => {
    try {
      return await listConfigurationEntries(
        (currentPage, pageSize) =>
          webinarService.listSurveyTemplates(workspaceId, webinarId, currentPage, pageSize),
        page,
        size,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchSurveyTemplate = createAsyncThunk<
  SurveyTemplate,
  { workspaceId: number; webinarId: number; templateId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchSurveyTemplate',
  async ({ workspaceId, webinarId, templateId }, { rejectWithValue }) => {
    try {
      const surveyTemplate = await webinarService.getSurveyTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      return surveyTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createSurveyTemplate = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: SurveyTemplateInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createSurveyTemplate',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      const createdTemplateId = await webinarService.createSurveyTemplate(
        workspaceId,
        webinarId,
        input,
      );
      if (!createdTemplateId) {
        return rejectWithValue({
          code: 'SURVEY_TEMPLATE_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }
      // dispatch(listSurveyTemplates({ workspaceId, webinarId }));
      return createdTemplateId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateSurveyTemplate = createAsyncThunk<
  SurveyTemplate,
  { workspaceId: number; webinarId: number; templateId: number; input: SurveyTemplateInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateSurveyTemplate',
  async ({ workspaceId, webinarId, templateId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateSurveyTemplate(workspaceId, webinarId, templateId, input);
      const surveyTemplate = await webinarService.getSurveyTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      // dispatch(listSurveyTemplates({ workspaceId, webinarId }));
      return surveyTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteSurveyTemplate = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; templateId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteSurveyTemplate',
  async ({ workspaceId, webinarId, templateId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteSurveyTemplate(workspaceId, webinarId, templateId);
      // dispatch(listSurveyTemplates({ workspaceId, webinarId }));
      return templateId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listSurveyQuestions = createAsyncThunk<
  SurveyQuestion[],
  { workspaceId: number; webinarId: number; templateId: number },
  { rejectValue: AppError }
>(
  'webinar/listSurveyQuestions',
  async ({ workspaceId, webinarId, templateId }, { rejectWithValue }) => {
    try {
      const questions = await webinarService.listSurveyQuestions(
        workspaceId,
        webinarId,
        templateId,
      );
      return questions;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createSurveyQuestion = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; templateId: number; input: SurveyQuestionInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createSurveyQuestion',
  async ({ workspaceId, webinarId, templateId, input }, { rejectWithValue }) => {
    try {
      const questionId = await webinarService.createSurveyQuestion(
        workspaceId,
        webinarId,
        templateId,
        input,
      );
      if (!questionId) {
        return rejectWithValue({
          code: 'SURVEY_QUESTION_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      // dispatch(listSurveyQuestions({ workspaceId, webinarId, templateId }));
      return questionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateSurveyQuestion = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    input: SurveyQuestionInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateSurveyQuestion',
  async ({ workspaceId, webinarId, templateId, questionId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateSurveyQuestion(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        input,
      );
      // dispatch(listSurveyQuestions({ workspaceId, webinarId, templateId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteSurveyQuestion = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; templateId: number; questionId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteSurveyQuestion',
  async ({ workspaceId, webinarId, templateId, questionId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteSurveyQuestion(workspaceId, webinarId, templateId, questionId);
      dispatch(listSurveyQuestions({ workspaceId, webinarId, templateId }));
      return questionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listSurveyAnswerOptions = createAsyncThunk<
  SurveyAnswerOption[],
  { workspaceId: number; webinarId: number; templateId: number; questionId: number },
  { rejectValue: AppError }
>(
  'webinar/listSurveyAnswerOptions',
  async ({ workspaceId, webinarId, templateId, questionId }, { rejectWithValue }) => {
    try {
      const options = await webinarService.listSurveyAnswerOptions(
        workspaceId,
        webinarId,
        templateId,
        questionId,
      );
      return options;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createSurveyAnswerOption = createAsyncThunk<
  number,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    input: SurveyAnswerOptionInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createSurveyAnswerOption',
  async ({ workspaceId, webinarId, templateId, questionId, input }, { rejectWithValue }) => {
    try {
      const optionId = await webinarService.createSurveyAnswerOption(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        input,
      );
      if (!optionId) {
        return rejectWithValue({
          code: 'SURVEY_ANSWER_OPTION_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      // dispatch(listSurveyAnswerOptions({ workspaceId, webinarId, templateId, questionId }));
      // dispatch(listSurveyQuestions({ workspaceId, webinarId, templateId }));
      return optionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateSurveyAnswerOption = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    optionId: number;
    input: SurveyAnswerOptionInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateSurveyAnswerOption',
  async (
    { workspaceId, webinarId, templateId, questionId, optionId, input },
    { rejectWithValue },
  ) => {
    try {
      await webinarService.updateSurveyAnswerOption(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        optionId,
        input,
      );
      // dispatch(listSurveyAnswerOptions({ workspaceId, webinarId, templateId, questionId }));
      // dispatch(listSurveyQuestions({ workspaceId, webinarId, templateId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteSurveyAnswerOption = createAsyncThunk<
  number,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    optionId: number;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteSurveyAnswerOption',
  async ({ workspaceId, webinarId, templateId, questionId, optionId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteSurveyAnswerOption(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        optionId,
      );
      return optionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Quit Page API
export const fetchQuitPage = createAsyncThunk<
  QuitPage,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchQuitPage', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    const quitPage = await webinarService.getQuitPage(workspaceId, webinarId);
    return quitPage;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const upsertQuitPage = createAsyncThunk<
  QuitPage,
  { workspaceId: number; webinarId: number; input: QuitPageInput },
  { rejectValue: AppError }
>('webinar/upsertQuitPage', async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
  try {
    await webinarService.upsertQuitPage(workspaceId, webinarId, input);
    const quitPage = await webinarService.getQuitPage(workspaceId, webinarId);
    return quitPage;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const listQuitPageReasons = createAsyncThunk<
  QuitPageReason[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/listQuitPageReasons',
  async ({ workspaceId, webinarId }, { rejectWithValue, dispatch }) => {
    try {
      const reasons = await webinarService.listQuitPageReasons(workspaceId, webinarId);
      dispatch(fetchQuitPage({ workspaceId, webinarId }));
      return reasons;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createQuitPageReason = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: QuitPageReasonInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createQuitPageReason',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      const reasonId = await webinarService.createQuitPageReason(workspaceId, webinarId, input);
      dispatch(fetchQuitPage({ workspaceId, webinarId }));
      return reasonId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateQuitPageReason = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    reasonId: number;
    input: QuitPageReasonInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateQuitPageReason',
  async ({ workspaceId, webinarId, reasonId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateQuitPageReason(workspaceId, webinarId, reasonId, input);
      // dispatch(fetchQuitPage({ workspaceId, webinarId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteQuitPageReason = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; reasonId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteQuitPageReason',
  async ({ workspaceId, webinarId, reasonId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteQuitPageReason(workspaceId, webinarId, reasonId);
      // dispatch(fetchQuitPage({ workspaceId, webinarId }));
      return reasonId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const uploadQuitPageReasonImage = createAsyncThunk<
  QuitPage,
  { workspaceId: number; webinarId: number; reasonId: number; file: File },
  { rejectValue: AppError }
>(
  'webinar/uploadQuitPageReasonImage',
  async ({ workspaceId, webinarId, reasonId, file }, { rejectWithValue }) => {
    try {
      await webinarService.uploadQuitPageReasonImage(workspaceId, webinarId, reasonId, file);
      const quitPage = await webinarService.getQuitPage(workspaceId, webinarId);
      return quitPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteQuitPageReasonImage = createAsyncThunk<
  QuitPage,
  { workspaceId: number; webinarId: number; reasonId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteQuitPageReasonImage',
  async ({ workspaceId, webinarId, reasonId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteQuitPageReasonImage(workspaceId, webinarId, reasonId);
      const quitPage = await webinarService.getQuitPage(workspaceId, webinarId);
      return quitPage;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Webinar Slide API
export const listWebinarSlides = createAsyncThunk<
  WebinarSlide[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listWebinarSlides',
  async ({ workspaceId, webinarId, page, size }, { rejectWithValue }) => {
    try {
      return await listConfigurationEntries(
        (currentPage, pageSize) =>
          webinarService.listSlides(workspaceId, webinarId, currentPage, pageSize),
        page,
        size,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchWebinarSlide = createAsyncThunk<
  WebinarSlide,
  { workspaceId: number; webinarId: number; slideId: number },
  { rejectValue: AppError }
>('webinar/fetchWebinarSlide', async ({ workspaceId, webinarId, slideId }, { rejectWithValue }) => {
  try {
    const slide = await webinarService.getSlide(workspaceId, webinarId, slideId);
    return slide;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const generateWebinarSlideUploadUrl = createAsyncThunk<
  SlideUploadInit,
  { workspaceId: number; webinarId: number; input: SlideUploadInitInput },
  { rejectValue: AppError }
>(
  'webinar/generateWebinarSlideUploadUrl',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      const uploadInit = await webinarService.generateSlideUploadUrl(workspaceId, webinarId, input);
      return uploadInit;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const finalizeWebinarSlideUpload = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: WebinarSlideFinalizeInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/finalizeWebinarSlideUpload',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      const slideId = await webinarService.finalizeSlideUpload(workspaceId, webinarId, input);
      if (!slideId) {
        return rejectWithValue({
          code: 'SLIDE_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      dispatch(listWebinarSlides({ workspaceId, webinarId }));
      return slideId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWebinarSlide = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; slideId: number },
  { rejectValue: AppError; dispatch: AppDispatch; state: RootState }
>(
  'webinar/deleteWebinarSlide',
  async ({ workspaceId, webinarId, slideId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      /** `currentWebinarSession` n'est peuplé que sur la page live (AdminLivePage). Depuis la
       * page de configuration, on retombe sur le webinar chargé via fetchWebinarById, sinon
       * l'événement RESOURCE_HIDE n'est jamais envoyé et les viewers restent bloqués sur le PDF. */
      const currentWebinar = state.webinar.currentWebinar;
      const sessionIdentifier =
        state.webinar.currentWebinarSession?.identifier ??
        (currentWebinar?.id === webinarId
          ? (currentWebinar.nextSessionIdentifier ?? currentWebinar.lastSessionIdentifier)
          : undefined);
      const sessionId =
        state.webinar.currentWebinarSession?.id ??
        (currentWebinar?.id === webinarId
          ? (currentWebinar.nextSessionId ?? currentWebinar.lastSessionId)
          : undefined);

      let wasLive = false;
      if (sessionId) {
        try {
          const freshStates = await dispatch(
            fetchAdminResourceState({ workspaceId, webinarId, sessionId }),
          ).unwrap();
          wasLive = freshStates.some(
            (s) => s.resourceType === 'WEBINAR_SLIDE' && s.resourceId === slideId && s.live,
          );
        } catch {
          /* si l'état frais est indisponible, on retombe sur le state local (mieux que rien) */
          wasLive = state.live.adminResourceStates.some(
            (s) => s.resourceType === 'WEBINAR_SLIDE' && s.resourceId === slideId && s.live,
          );
        }
      }

      if (sessionIdentifier) {
        await dispatch(
          triggerAdminEvent({
            workspaceId,
            webinarId,
            sessionIdentifier,
            input: {
              eventType: 'RESOURCE_HIDE',
              resourceType: 'WEBINAR_SLIDE',
              resourceId: slideId,
              payloadJson: JSON.stringify({ timeNow: Date.now() }),
            },
          }),
        );
      }

      if (wasLive && sessionId) {
        await dispatch(
          updateSessionLayout({
            workspaceId,
            webinarId,
            sessionId,
            input: { layout: 'grid', activeAsset: null },
          }),
        );
      }

      await webinarService.deleteSlide(workspaceId, webinarId, slideId);
      dispatch(listWebinarSlides({ workspaceId, webinarId }));
      return slideId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listWebinarSlideNotes = createAsyncThunk<
  WebinarSlideNote[],
  { workspaceId: number; webinarId: number; slideId: number },
  { rejectValue: AppError }
>(
  'webinar/listWebinarSlideNotes',
  async ({ workspaceId, webinarId, slideId }, { rejectWithValue }) => {
    try {
      const notes = await webinarService.listSlideNotes(workspaceId, webinarId, slideId);
      return notes;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const bulkUpsertWebinarSlideNotes = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; slideId: number; input: SlideNotesBulkInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/bulkUpsertWebinarSlideNotes',
  async ({ workspaceId, webinarId, slideId, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.bulkUpsertSlideNotes(workspaceId, webinarId, slideId, input);
      dispatch(listWebinarSlideNotes({ workspaceId, webinarId, slideId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateWebinarSlideNote = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    slideId: number;
    pageNumber: number;
    input: SlideNoteUpdateInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateWebinarSlideNote',
  async ({ workspaceId, webinarId, slideId, pageNumber, input }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.updateSlideNote(workspaceId, webinarId, slideId, pageNumber, input);
      dispatch(listWebinarSlideNotes({ workspaceId, webinarId, slideId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Webinar Resource API
export const listWebinarResources = createAsyncThunk<
  PageResponse<WebinarResource>,
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listWebinarResources',
  async ({ workspaceId, webinarId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      return await webinarService.listResources(workspaceId, webinarId, page, size);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const initWebinarResourceUpload = createAsyncThunk<
  WebinarResourceUploadInit,
  { workspaceId: number; webinarId: number; input: ResourceUploadInitInput },
  { rejectValue: AppError }
>(
  'webinar/initWebinarResourceUpload',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      const uploadInit = await webinarService.initResourceUpload(workspaceId, webinarId, input);
      return uploadInit;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const finalizeWebinarResourceUpload = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: WebinarResourceFinalizeInput },
  { rejectValue: AppError }
>(
  'webinar/finalizeWebinarResourceUpload',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.finalizeResourceUpload(workspaceId, webinarId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWebinarResource = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; resourceId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteWebinarResource',
  async ({ workspaceId, webinarId, resourceId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteResource(workspaceId, webinarId, resourceId);
      return resourceId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// Quiz Template Thunks
export const listQuizTemplates = createAsyncThunk<
  QuizTemplate[],
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listQuizTemplates',
  async ({ workspaceId, webinarId, page, size }, { rejectWithValue }) => {
    try {
      return await listConfigurationEntries(
        (currentPage, pageSize) =>
          webinarService.listQuizTemplates(workspaceId, webinarId, currentPage, pageSize),
        page,
        size,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchQuizTemplate = createAsyncThunk<
  QuizTemplate,
  { workspaceId: number; webinarId: number; templateId: number },
  { rejectValue: AppError }
>(
  'webinar/fetchQuizTemplate',
  async ({ workspaceId, webinarId, templateId }, { rejectWithValue }) => {
    try {
      const quizTemplate = await webinarService.getQuizTemplate(workspaceId, webinarId, templateId);
      return quizTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createQuizTemplate = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: QuizTemplateInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>('webinar/createQuizTemplate', async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
  try {
    const createdTemplateId = await webinarService.createQuizTemplate(
      workspaceId,
      webinarId,
      input,
    );
    if (!createdTemplateId) {
      return rejectWithValue({
        code: 'QUIZ_TEMPLATE_ID_NOT_FOUND',
        message: 'errors.generic',
      });
    }

    // dispatch(listQuizTemplates({ workspaceId, webinarId }));
    return createdTemplateId;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const updateQuizTemplate = createAsyncThunk<
  QuizTemplate,
  { workspaceId: number; webinarId: number; templateId: number; input: QuizTemplateInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateQuizTemplate',
  async ({ workspaceId, webinarId, templateId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateQuizTemplate(workspaceId, webinarId, templateId, input);
      const quizTemplate = await webinarService.getQuizTemplate(workspaceId, webinarId, templateId);
      // dispatch(listQuizTemplates({ workspaceId, webinarId }));
      return quizTemplate;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteQuizTemplate = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; templateId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteQuizTemplate',
  async ({ workspaceId, webinarId, templateId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteQuizTemplate(workspaceId, webinarId, templateId);
      return templateId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listQuizQuestions = createAsyncThunk<
  QuizQuestion[],
  { workspaceId: number; webinarId: number; templateId: number },
  { rejectValue: AppError }
>(
  'webinar/listQuizQuestions',
  async ({ workspaceId, webinarId, templateId }, { rejectWithValue }) => {
    try {
      const questions = await webinarService.listQuizQuestions(workspaceId, webinarId, templateId);
      return questions;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createQuizQuestion = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; templateId: number; input: QuizQuestionInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createQuizQuestion',
  async ({ workspaceId, webinarId, templateId, input }, { rejectWithValue }) => {
    try {
      const questionId = await webinarService.createQuizQuestion(
        workspaceId,
        webinarId,
        templateId,
        input,
      );
      if (!questionId) {
        return rejectWithValue({
          code: 'QUIZ_QUESTION_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      // dispatch(listQuizQuestions({ workspaceId, webinarId, templateId }));
      // dispatch(fetchQuizTemplate({ workspaceId, webinarId, templateId }));
      return questionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateQuizQuestion = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    input: QuizQuestionInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateQuizQuestion',
  async ({ workspaceId, webinarId, templateId, questionId, input }, { rejectWithValue }) => {
    try {
      await webinarService.updateQuizQuestion(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        input,
      );
      // dispatch(listQuizQuestions({ workspaceId, webinarId, templateId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteQuizQuestion = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; templateId: number; questionId: number },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteQuizQuestion',
  async ({ workspaceId, webinarId, templateId, questionId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.deleteQuizQuestion(workspaceId, webinarId, templateId, questionId);
      dispatch(listQuizQuestions({ workspaceId, webinarId, templateId }));
      return questionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listQuizAnswerOptions = createAsyncThunk<
  QuizAnswerOption[],
  { workspaceId: number; webinarId: number; templateId: number; questionId: number },
  { rejectValue: AppError }
>(
  'webinar/listQuizAnswerOptions',
  async ({ workspaceId, webinarId, templateId, questionId }, { rejectWithValue }) => {
    try {
      const options = await webinarService.listQuizAnswerOptions(
        workspaceId,
        webinarId,
        templateId,
        questionId,
      );
      return options;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const createQuizAnswerOption = createAsyncThunk<
  number,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    input: QuizAnswerOptionInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createQuizAnswerOption',
  async ({ workspaceId, webinarId, templateId, questionId, input }, { rejectWithValue }) => {
    try {
      const optionId = await webinarService.createQuizAnswerOption(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        input,
      );
      if (!optionId) {
        return rejectWithValue({
          code: 'QUIZ_ANSWER_OPTION_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }
      // dispatch(listQuizAnswerOptions({ workspaceId, webinarId, templateId, questionId }));
      // dispatch(listQuizQuestions({ workspaceId, webinarId, templateId }));
      // dispatch(fetchQuizTemplate({ workspaceId, webinarId, templateId }));
      return optionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateQuizAnswerOption = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    optionId: number;
    input: QuizAnswerOptionInput;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/updateQuizAnswerOption',
  async (
    { workspaceId, webinarId, templateId, questionId, optionId, input },
    { rejectWithValue },
  ) => {
    try {
      await webinarService.updateQuizAnswerOption(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        optionId,
        input,
      );
      // dispatch(listQuizAnswerOptions({ workspaceId, webinarId, templateId, questionId }));
      // dispatch(listQuizQuestions({ workspaceId, webinarId, templateId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteQuizAnswerOption = createAsyncThunk<
  number,
  {
    workspaceId: number;
    webinarId: number;
    templateId: number;
    questionId: number;
    optionId: number;
  },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/deleteQuizAnswerOption',
  async (
    { workspaceId, webinarId, templateId, questionId, optionId },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await webinarService.deleteQuizAnswerOption(
        workspaceId,
        webinarId,
        templateId,
        questionId,
        optionId,
      );
      dispatch(listQuizAnswerOptions({ workspaceId, webinarId, templateId, questionId }));
      dispatch(listQuizQuestions({ workspaceId, webinarId, templateId }));
      return optionId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const fetchIntegrationsMe = createAsyncThunk<
  TechnicalAuthUser,
  void,
  { rejectValue: AppError }
>('webinar/fetchIntegrationsMe', async (_, { rejectWithValue }) => {
  try {
    const me = await webinarService.getIntegrationsMe();
    return me;
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchEvaluationTemplate = createAsyncThunk<
  EvaluationTemplate,
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchEvaluationTemplate', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    return await webinarService.getEvaluationTemplate(workspaceId, webinarId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchEvaluationQuestions = createAsyncThunk<
  EvaluationQuestion[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/fetchEvaluationQuestions', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    return await webinarService.getEvaluationQuestions(workspaceId, webinarId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const createEvaluationQuestion = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; input: EvaluationQuestionInput },
  { rejectValue: AppError }
>(
  'webinar/createEvaluationQuestion',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.createEvaluationQuestion(workspaceId, webinarId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateEvaluationTemplate = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; input: EvaluationTemplateInput },
  { rejectValue: AppError }
>(
  'webinar/updateEvaluationTemplate',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.updateEvaluationTemplate(workspaceId, webinarId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateEvaluationQuestion = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    questionId: number;
    input: EvaluationQuestionInput;
  },
  { rejectValue: AppError }
>(
  'webinar/updateEvaluationQuestion',
  async ({ workspaceId, webinarId, questionId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.updateEvaluationQuestion(
        workspaceId,
        webinarId,
        questionId,
        input,
      );
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteEvaluationQuestion = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; questionId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteEvaluationQuestion',
  async ({ workspaceId, webinarId, questionId }, { rejectWithValue }) => {
    try {
      return await webinarService.deleteEvaluationQuestion(workspaceId, webinarId, questionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listAutomationTriggers = createAsyncThunk<
  WebinarIntegrationTrigger[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/listAutomationTriggers', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    return await webinarService.listAutomationTriggers(workspaceId, webinarId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const createAutomationTrigger = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: WebinarIntegrationTriggerInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createAutomationTrigger',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      const triggerId = await webinarService.createAutomationTrigger(workspaceId, webinarId, input);
      if (!triggerId) {
        return rejectWithValue({
          code: 'AUTOMATION_TRIGGER_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      dispatch(listAutomationTriggers({ workspaceId, webinarId }));
      return triggerId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateAutomationTrigger = createAsyncThunk<
  void,
  {
    workspaceId: number;
    webinarId: number;
    triggerId: number;
    input: WebinarIntegrationTriggerInput;
  },
  { rejectValue: AppError }
>(
  'webinar/updateAutomationTrigger',
  async ({ workspaceId, webinarId, triggerId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.updateAutomationTrigger(workspaceId, webinarId, triggerId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteAutomationTrigger = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; triggerId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteAutomationTrigger',
  async ({ workspaceId, webinarId, triggerId }, { rejectWithValue }) => {
    try {
      return await webinarService.deleteAutomationTrigger(workspaceId, webinarId, triggerId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const listWebhooks = createAsyncThunk<
  WebhookDto[],
  { workspaceId: number; webinarId: number },
  { rejectValue: AppError }
>('webinar/listWebhooks', async ({ workspaceId, webinarId }, { rejectWithValue }) => {
  try {
    return await webinarService.listWebhooks(workspaceId, webinarId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const createWebhook = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; input: WebhookInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createWebhook',
  async ({ workspaceId, webinarId, input }, { rejectWithValue, dispatch }) => {
    try {
      const webhookId = await webinarService.createWebhook(workspaceId, webinarId, input);
      if (!webhookId) {
        return rejectWithValue({
          code: 'WEBHOOK_ID_NOT_FOUND',
          message: 'errors.generic',
        });
      }

      dispatch(listWebhooks({ workspaceId, webinarId }));
      return webhookId;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const updateWebhook = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; webhookId: number; input: WebhookInput },
  { rejectValue: AppError }
>(
  'webinar/updateWebhook',
  async ({ workspaceId, webinarId, webhookId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.updateWebhook(workspaceId, webinarId, webhookId, input);
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const deleteWebhook = createAsyncThunk<
  void,
  { workspaceId: number; webinarId: number; webhookId: number },
  { rejectValue: AppError }
>('webinar/deleteWebhook', async ({ workspaceId, webinarId, webhookId }, { rejectWithValue }) => {
  try {
    return await webinarService.deleteWebhook(workspaceId, webinarId, webhookId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey =
        err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
          ? err.message
          : mapApiError(err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey =
      e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
        ? e.message
        : e.code
          ? mapApiError(e.code, e.status)
          : 'errors.generic';

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const fetchIntegrationApiKeys = createAsyncThunk<
  IntegrationApiKeyDto[],
  { workspaceId: number },
  { rejectValue: AppError }
>('webinar/fetchIntegrationApiKeys', async ({ workspaceId }, { rejectWithValue }) => {
  try {
    return await webinarService.getIntegrationApiKeys(workspaceId);
  } catch (err) {
    if (err instanceof HttpAppError) {
      const messageKey = mapErrorMessage(err.message, err.code, err.status);

      return rejectWithValue({
        code: err.code,
        message: messageKey,
        details: err.details,
      });
    }

    const e = err as { code?: string; message?: string; status?: number };
    const messageKey = mapErrorMessage(e.message, e.code, e.status);

    return rejectWithValue({
      code: e.code ?? 'UNKNOWN',
      message: messageKey,
    });
  }
});

export const createIntegrationApiKey = createAsyncThunk<
  IntegrationApiKeyCreatedDto,
  { workspaceId: number; input: CreateIntegrationApiKeyInput },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/createIntegrationApiKey',
  async ({ workspaceId, input }, { rejectWithValue, dispatch }) => {
    try {
      const createdApiKey = await webinarService.createIntegrationApiKey(workspaceId, input);
      dispatch(fetchIntegrationApiKeys({ workspaceId }));
      return createdApiKey;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const rotateIntegrationApiKey = createAsyncThunk<
  IntegrationApiKeyCreatedDto,
  { workspaceId: number; apiKeyId: string },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/rotateIntegrationApiKey',
  async ({ workspaceId, apiKeyId }, { rejectWithValue, dispatch }) => {
    try {
      const rotatedApiKey = await webinarService.rotateIntegrationApiKey(workspaceId, apiKeyId);
      dispatch(fetchIntegrationApiKeys({ workspaceId }));
      return rotatedApiKey;
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

export const revokeIntegrationApiKey = createAsyncThunk<
  void,
  { workspaceId: number; apiKeyId: string },
  { rejectValue: AppError; dispatch: AppDispatch }
>(
  'webinar/revokeIntegrationApiKey',
  async ({ workspaceId, apiKeyId }, { rejectWithValue, dispatch }) => {
    try {
      await webinarService.revokeIntegrationApiKey(workspaceId, apiKeyId);
      dispatch(fetchIntegrationApiKeys({ workspaceId }));
    } catch (err) {
      if (err instanceof HttpAppError) {
        const messageKey =
          err.message && (err.message.startsWith('errors.') || err.message.startsWith('HTTP.'))
            ? err.message
            : mapApiError(err.code, err.status);

        return rejectWithValue({
          code: err.code,
          message: messageKey,
          details: err.details,
        });
      }

      const e = err as { code?: string; message?: string; status?: number };
      const messageKey =
        e.message && (e.message.startsWith('errors.') || e.message.startsWith('HTTP.'))
          ? e.message
          : e.code
            ? mapApiError(e.code, e.status)
            : 'errors.generic';

      return rejectWithValue({
        code: e.code ?? 'UNKNOWN',
        message: messageKey,
      });
    }
  },
);

// ---------------------------------------------------------------------------
// Fichiers & Liens (SharedResource)
// ---------------------------------------------------------------------------

export const listSharedResources = createAsyncThunk<
  SharedResourcePage,
  { workspaceId: number; webinarId: number; page?: number; size?: number },
  { rejectValue: AppError }
>(
  'webinar/listSharedResources',
  async ({ workspaceId, webinarId, page = 0, size = 20 }, { rejectWithValue }) => {
    try {
      return await webinarService.listSharedResources(workspaceId, webinarId, page, size);
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const initSharedResourceFileUpload = createAsyncThunk<
  SharedResourceUploadInit,
  { workspaceId: number; webinarId: number; input: SharedResourceFileUploadInitInput },
  { rejectValue: AppError }
>(
  'webinar/initSharedResourceFileUpload',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.initSharedResourceFileUpload(workspaceId, webinarId, input);
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const finalizeSharedResourceFileUpload = createAsyncThunk<
  SharedResource,
  { workspaceId: number; webinarId: number; sharedResourceId: number },
  { rejectValue: AppError }
>(
  'webinar/finalizeSharedResourceFileUpload',
  async ({ workspaceId, webinarId, sharedResourceId }, { rejectWithValue }) => {
    try {
      return await webinarService.finalizeSharedResourceFileUpload(
        workspaceId,
        webinarId,
        sharedResourceId,
      );
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const createSharedResourceLink = createAsyncThunk<
  SharedResource,
  { workspaceId: number; webinarId: number; input: SharedResourceLinkInput },
  { rejectValue: AppError }
>(
  'webinar/createSharedResourceLink',
  async ({ workspaceId, webinarId, input }, { rejectWithValue }) => {
    try {
      return await webinarService.createSharedResourceLink(workspaceId, webinarId, input);
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const deleteSharedResource = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; sharedResourceId: number },
  { rejectValue: AppError }
>(
  'webinar/deleteSharedResource',
  async ({ workspaceId, webinarId, sharedResourceId }, { rejectWithValue }) => {
    try {
      await webinarService.deleteSharedResource(workspaceId, webinarId, sharedResourceId);
      return sharedResourceId;
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const adminShowSharedResource = createAsyncThunk<
  SharedResourcePublic,
  { workspaceId: number; webinarId: number; sessionIdentifier: string; sharedResourceId: number },
  { rejectValue: AppError }
>(
  'webinar/adminShowSharedResource',
  async ({ workspaceId, webinarId, sessionIdentifier, sharedResourceId }, { rejectWithValue }) => {
    try {
      return await webinarService.adminShowSharedResource(
        workspaceId,
        webinarId,
        sessionIdentifier,
        sharedResourceId,
      );
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const adminHideSharedResource = createAsyncThunk<
  number,
  { workspaceId: number; webinarId: number; sessionIdentifier: string; sharedResourceId: number },
  { rejectValue: AppError }
>(
  'webinar/adminHideSharedResource',
  async ({ workspaceId, webinarId, sessionIdentifier, sharedResourceId }, { rejectWithValue }) => {
    try {
      await webinarService.adminHideSharedResource(
        workspaceId,
        webinarId,
        sessionIdentifier,
        sharedResourceId,
      );
      return sharedResourceId;
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const fetchCurrentSharedResources = createAsyncThunk<
  SharedResourcePublic[],
  { webinarId: number; sessionIdentifier: string; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchCurrentSharedResources',
  async ({ webinarId, sessionIdentifier, authorization }, { rejectWithValue }) => {
    try {
      return await webinarService.getCurrentSharedResources(
        webinarId,
        sessionIdentifier,
        authorization,
      );
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);

export const fetchSharedResourceDownloadUrl = createAsyncThunk<
  SharedResourceDownloadUrl,
  { webinarId: number; sessionIdentifier: string; sharedResourceId: number; authorization: string },
  { rejectValue: AppError }
>(
  'webinar/fetchSharedResourceDownloadUrl',
  async (
    { webinarId, sessionIdentifier, sharedResourceId, authorization },
    { rejectWithValue },
  ) => {
    try {
      return await webinarService.getSharedResourceDownloadUrl(
        webinarId,
        sessionIdentifier,
        sharedResourceId,
        authorization,
      );
    } catch (err) {
      return rejectWithValue(toRejectValue(err));
    }
  },
);
